/**
 * Smart capture — ARCH-node-mcp (Core layer)
 * Chain: FEAT-MCP-001 (MCP Server), ARCH-flow-smart-capture
 * Rules: SOS-016 (governed draft-first), SOS-015 (unique IDs within scope)
 */

import { McpServer } from "@modelcontextprotocol/sdk/server/mcp.js";
import { z } from "zod/v3";
import { kernelQuery, kernelMutation, kernelCall, getWorkspaceContext, refreshWorkspaceGovernanceMode, requireWriteAccess, recordSessionActivity, getAgentSessionId, cacheScope, runWithToolContext } from "../client.js";
import { dispatchDiscriminated, type ActionParamSpec } from "../compound-tool.js";
import { ScopedCache, COLLECTION_CACHE_TTL_MS } from "../lib/scopedCache.js";
import {
  trackCaptureClassifierAutoRouted,
  trackCaptureClassifierEvaluated,
  trackCaptureClassifierFallback,
  trackCaptureQualityHints,
  trackCaptureRelationSuggestions,
  trackQualityVerdict,
  trackChainEntryCommitted,
  trackCollectionClassified,
  trackFieldGuidanceApplied,
  trackClassifierDivergence,
  type ClassifierReasonCategory,
} from "../analytics.js";
import { extractPreview } from "./knowledge-helpers.js";
// WP-465 surface parity (TEN-2458): import from coherency-refusal.ts (not knowledge.ts) to avoid circular dep.
import { coherencyRefusedResult } from "./coherency-refusal.js";
import { trackWriteTool } from "../tool-surface.js";
import { resolveGapsForEntry } from "../gap-store.js";
import {
  CLASSIFIABLE_COLLECTIONS,
  type ClassifiableCollectionSlug,
} from "./smart-capture-routing.js";
import { resolveCollection, type ResolvedCollection } from "../lib/resolveCollection.js";
import { thinWrapper, success, failure, parseOrFail, type NextAction, type EnvelopeError, type CaptureContract, type ToolResult } from "../envelope.js";
import { FIELD_TYPE_DEFAULTS, type FieldType } from "../lib/fieldTypes.js";
import { formatFieldGuidance } from "../lib/formatFieldGuidance.js";
import { inferSourceDate } from "../lib/inferSourceDate.js";
import {
  getCollections,
  getCollectionBySlug,
  isGoverned,
  isHubCollection,
  type CollectionDoc,
} from "../lib/collectionCache.js";

export {
  CLASSIFIER_AUTO_ROUTE_THRESHOLD,
  CLASSIFIABLE_COLLECTIONS,
  isClassificationAmbiguous,
  type ClassifiableCollectionSlug,
  type ClassificationResult,
} from "./smart-capture-routing.js";

// ── Collection Workflow Profiles ────────────────────────────────────────────

interface FieldDefault {
  key: string;
  value: unknown | "today" | "infer";
}

interface CollectionProfile {
  governedDraft: boolean;
  defaults: FieldDefault[];
  descriptionField: string;
  recommendedRelationTypes: string[];
  inferField?: (ctx: CaptureContext) => Record<string, unknown>;
}

interface CaptureContext {
  collection: string;
  name: string;
  description: string;
  context?: string;
  data: Record<string, unknown>;
  entryId: string;
  canonicalKey?: string;
  linksCreated: LinkResult[];
  linksSuggested: LinkSuggestion[];
  collectionFields: Array<{ key: string; type: string; required?: boolean; options?: string[]; label?: string; writingGuidance?: string; writingExamples?: string[]; minLength?: number; maxLength?: number }>;
}

interface LinkResult {
  targetEntryId: string;
  targetName: string;
  targetCollection: string;
  relationType: string;
  linkReason?: string;
}

interface LinkSuggestion {
  entryId?: string;
  name: string;
  collection: string;
  reason: string;
  preview: string;
}

function normalizeMatchText(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function tokenizeMatchText(value: string): string[] {
  return normalizeMatchText(value)
    .split(" ")
    .filter((token) => token.length >= 3 && !STOP_WORDS.has(token));
}

function scoreOptionMatch(
  normalizedText: string,
  textTokens: Set<string>,
  option: string,
): number {
  const normalizedOption = normalizeMatchText(option);
  if (!normalizedOption) return 0;
  if (normalizedText.includes(normalizedOption)) return 100;

  const optionTokens = tokenizeMatchText(option);
  if (optionTokens.length === 0) return 0;

  const matchedTokens = optionTokens.filter((token) => textTokens.has(token)).length;
  if (matchedTokens === 0) return 0;

  const ratio = matchedTokens / optionTokens.length;
  return ratio >= 0.5 ? ratio * 10 : 0;
}

function inferFieldOption(
  text: string,
  collectionFields: Array<{ key: string; type: string; options?: string[] }>,
  fieldKeys: string[],
): string {
  const normalizedText = normalizeMatchText(text);
  if (!normalizedText) return "";

  const textTokens = new Set(tokenizeMatchText(text));
  let bestOption = "";
  let bestScore = 0;

  for (const field of collectionFields) {
    if (!fieldKeys.includes(field.key) || !field.options?.length) continue;
    for (const option of field.options) {
      const score = scoreOptionMatch(normalizedText, textTokens, option);
      if (score > bestScore) {
        bestScore = score;
        bestOption = option;
      }
    }
  }

  return bestOption;
}


// ── Strategy Category Inference (FEAT-128) ──────────────────────────────────

const STRATEGY_CATEGORY_INFERENCE_THRESHOLD = 2;

const STRATEGY_CATEGORY_SIGNALS: readonly [string, readonly RegExp[]][] = [
  ["business-model", [/pricing/, /revenue/, /unit economics/, /cost structure/, /monetiz/, /margin/, /per.?seat/, /subscription/, /freemium/]],
  ["vision",         [/vision/, /aspirational/, /future state/, /world where/]],
  ["purpose",        [/purpose/, /why we exist/, /mission/, /reason for being/]],
  ["goal",           [/goal/, /metric/, /target/, /kpi/, /okr/, /critical number/, /measur/]],
  ["principle",      [/we believe/, /guiding principle/, /core belief/, /philosophy/]],
  ["product-area",   [/product area/, /module/, /surface/, /capability area/]],
  ["audience",       [/audience/, /persona/, /user segment/, /icp/, /target market/]],
  ["insight",        [/insight/, /we learned/, /we observed/, /pattern/]],
  ["opportunity",    [/opportunity/, /whitespace/, /gap/, /underserved/]],
] as const;

/**
 * Infer strategy sub-category from entry text. Returns null when confidence
 * is too low (exactly 1 signal match) — the caller decides how to handle
 * the abstention. Returns "strategy" as generic fallback when no signals
 * match at all (entry is in strategy collection but has no sub-type signals).
 *
 * ecosystem-layer is intentionally excluded: too niche to distinguish from
 * product-area by keywords alone — must be set explicitly via data.category.
 */
export function inferStrategyCategory(name: string, description: string): string | null {
  const text = `${name} ${description}`.toLowerCase();
  let bestCategory: string | null = null;
  let bestScore = 0;
  for (const [cat, signals] of STRATEGY_CATEGORY_SIGNALS) {
    const score = signals.reduce((s, rx) => s + (rx.test(text) ? 1 : 0), 0);
    if (score > bestScore) { bestScore = score; bestCategory = cat; }
  }
  if (bestCategory && bestScore >= STRATEGY_CATEGORY_INFERENCE_THRESHOLD) return bestCategory;
  if (bestScore === 0) return "strategy";
  return null;
}

// ── Collection Profiles ─────────────────────────────────────────────────────
// BR-183 / E4.2: Runtime profiles are derived from collection documents. The
// generic fallback intentionally avoids slug-specific defaults, relation
// policies, and inference because collection docs do not expose those sources.

const GENERIC_PROFILE: CollectionProfile = {
  governedDraft: false,
  descriptionField: "description",
  defaults: [],
  recommendedRelationTypes: ["related_to", "references"],
};

/**
 * E4.2: Build a runtime profile from collection metadata. Do not infer
 * relation policies, default values, or field-population policies from slug.
 *
 * WP-480 S1 (FEAT-1358): quality is judged ONCE, server-side (STD-101 / DEC-32
 * thin wrapper), so this profile carries no client scorecard — it keeps only
 * description-field resolution and relation defaults.
 */
async function buildRuntimeProfile(slug: string): Promise<CollectionProfile> {
  const col = await getCollectionBySlug(slug);
  if (!col) return GENERIC_PROFILE;

  const fields = col.fields ?? [];
  const layoutDescriptionField = fields.find(
    (field) => field.zone === "body" || field.displayHint === "textarea" || field.displayHint === "section",
  )?.key;
  const descriptionField = col.descriptionFieldKey || layoutDescriptionField || "description";

  return {
    governedDraft: col.governed === true,
    descriptionField,
    defaults: [],
    recommendedRelationTypes: GENERIC_PROFILE.recommendedRelationTypes,
  };
}

/**
 * Per-scope profile cache (keyed by collection slug within a scope).
 * Note: TTL is stamped when a scope's Map is first created; later map.set adds
 * entries without refreshing it, so all profiles in a scope expire ≤TTL after the
 * first one. Fail-safe toward freshness (can only expire sooner); staleness
 * ceiling stays COLLECTION_CACHE_TTL_MS.
 */
const profileCache = new ScopedCache<Map<string, CollectionProfile>>(COLLECTION_CACHE_TTL_MS);

/**
 * Get a collection's profile for the current scope, cached with a short TTL.
 */
export async function getProfile(slug: string): Promise<CollectionProfile> {
  const scope = cacheScope();
  let map = profileCache.get(scope);
  if (!map) {
    map = new Map();
    profileCache.set(scope, map);
  }
  const cached = map.get(slug);
  if (cached) return cached;
  const profile = await buildRuntimeProfile(slug);
  map.set(slug, profile);
  return profile;
}

/** Reset all profile caches — test hook + full flush. */
export function resetProfileCache(): void {
  profileCache.clear();
}

/**
 * Drop the current scope's profile cache. Profiles derive their descriptionField and
 * quality criteria from collection fields, so a collection write in this scope must drop
 * them too — otherwise the next capture uses the pre-write profile until the TTL expires.
 */
export function invalidateProfileCacheForScope(): void {
  profileCache.delete(cacheScope());
}

// ── Auto-Linking Logic ──────────────────────────────────────────────────────

function extractSearchTerms(name: string, description: string): string {
  const text = `${name} ${description}`;
  return text
    .replace(/[^\w\s]/g, " ")
    .split(/\s+/)
    .filter((w) => w.length > 3)
    .slice(0, 8)
    .join(" ");
}

interface LinkConfidenceResult {
  score: number;
  reason: string;
}

// BET-281: hub slugs get higher link-confidence. Per-scope + TTL (TEN-2415).
const hubCache = new ScopedCache<Set<string>>(COLLECTION_CACHE_TTL_MS);

/** Resolve (and cache) the current scope's hub-collection slug set. */
export async function populateHubCache(): Promise<Set<string>> {
  const scope = cacheScope();
  const hit = hubCache.get(scope);
  if (hit) return hit;
  const cols = await getCollections();
  const set = new Set(
    cols
      .filter((c) => c.governanceRole === "steering" || c.thinkingLayer === "strategy")
      .map((c) => c.slug),
  );
  hubCache.set(scope, set);
  return set;
}

/** Reset all hub caches — test hook + full flush. */
export function resetHubCache(): void {
  hubCache.clear();
}

/**
 * Drop the current scope's hub-slug set. The set derives from collection governanceRole /
 * thinkingLayer, so a collection write in this scope must drop it too — otherwise link
 * confidence keeps scoring against the pre-write hub set until the TTL expires.
 */
export function invalidateHubCacheForScope(): void {
  hubCache.delete(cacheScope());
}

function computeLinkConfidence(
  candidate: { name: string; data?: any; entryId?: string },
  sourceName: string,
  sourceDescription: string,
  sourceCollection: string,
  candidateCollection: string,
  hubSlugs: Set<string>,
): LinkConfidenceResult {
  const text = `${sourceName} ${sourceDescription}`.toLowerCase();
  const candidateName = candidate.name.toLowerCase();
  let score = 0;
  const reasons: string[] = [];

  if (text.includes(candidateName) && candidateName.length > 3) {
    score += 40;
    reasons.push("name match");
  }

  const candidateWords = candidateName.split(/\s+/).filter((w) => w.length > 3);
  const matchingWords = candidateWords.filter((w) => text.includes(w));
  const wordScore = (matchingWords.length / Math.max(candidateWords.length, 1)) * 30;
  score += wordScore;
  if (matchingWords.length > 0) {
    reasons.push(`word overlap (${matchingWords.slice(0, 3).join(", ")})`);
  }

  // BET-281: Hub collections derived from thinkingLayer === 'strategy' via collectionCache.
  if (hubSlugs.has(candidateCollection)) {
    score += 15;
    reasons.push("hub collection");
  }

  if (candidateCollection !== sourceCollection) {
    score += 10;
    reasons.push("cross-collection");
  }

  const finalScore = Math.min(score, 100);
  const reason = reasons.length > 0 ? reasons.join(" + ") : "low relevance";
  return { score: finalScore, reason };
}

interface RelationTypeResult {
  type: string;
  reason: string;
}

function inferRelationType(
  _sourceCollection: string,
  _targetCollection: string,
  profile: CollectionProfile,
): RelationTypeResult {
  const type = profile.recommendedRelationTypes[0] ?? "related_to";
  const reason = `profile default (${type})`;
  return { type, reason };
}


// ── Tool Registration ───────────────────────────────────────────────────────

// BET-281: GOVERNED_COLLECTIONS replaced by runtime isGoverned() from collectionCache.
// The `governed` field on each collection doc is the SSOT.

const AUTO_LINK_CONFIDENCE_THRESHOLD = 35;
const MAX_AUTO_LINKS = 5;
const MAX_SUGGESTIONS = 5;

/** BR-111: entryId only for business-rules and standards; format BR-NNN or STD-NNN. All other collections use auto-generated IDs. */
const BR_STD_ENTRY_ID_REGEX = /^(BR|STD)-\d+$/;
const entryIdSchema = z.string().regex(BR_STD_ENTRY_ID_REGEX).optional().describe(
  "Only for business-rules and standards. Must match BR-NNN or STD-NNN. Omit for all other collections — IDs are auto-generated."
);

export const captureSchema = z.object({
  collection: z.string().optional().describe("Collection slug, e.g. 'tensions', 'business-rules', 'glossary', 'decisions'. Optional — classifier auto-routes when omitted."),
  name: z.string().describe("Display name — be specific (e.g. 'Convex adjacency list won't scale for graph traversal')"),
  description: z.string().describe("Full context — what's happening, why it matters, what you observed"),
  context: z.string().optional().describe("Optional additional context (e.g. 'Observed during context gather calls taking 700ms+')"),
  entryId: entryIdSchema,
  canonicalKey: z.string().optional().describe("Semantic type (e.g. 'decision', 'tension', 'vision'). Auto-assigned from collection if omitted."),
  data: z.record(z.unknown()).optional().describe("Explicit field values when you know the schema (e.g. canonical_key, cardinality_rule, required_fields). Merged with inferred values; user-provided wins."),
  links: z.array(z.object({
    to: z.string().describe("Target entry ID (e.g. '<PREFIX>-<n>')"),
    type: z.string().describe("Relation type (e.g. 'governs', 'related_to', 'informs')"),
  })).optional().describe("Relations to create after capture. Skips auto-link discovery when provided."),
  autoCommit: z.boolean().optional().describe(
    "If true, commits the entry immediately after capture + linking. " +
    "If omitted, Open mode workspaces auto-commit by default and consensus/role modes stay draft-first."
  ),
  sourceRef: z.string().optional().describe("URI or path of the source document backing this entry (e.g. 'meeting-2026-03-28.md', 'import://batch-5'). Stored as top-level entry field, not in data."),
  sourceExcerpt: z.string().optional().describe("Verbatim excerpt from the source that backs this entry's claims. Stored as top-level entry field, not in data."),
  // WP-316 S3: Preview gate — dry-run mode. Returns what would happen, no DB writes.
  preview: z.boolean().optional().describe("If true, validates the capture without writing. Returns what would happen. Default false."),
  // WP-318 S2: Pre-write grounding — run link suggestion before creating entry.
  suggestOnly: z.boolean().optional().describe(
    "If true, runs pre-write grounding (suggestLinksForCapture) and returns a groundingReport WITHOUT creating any entry. " +
    "Use to preview graph participation before accepting. Default false."
  ),
  // WP-318 S2: Format for groundingReport in response.
  format: z.enum(["agent", "human"]).optional().describe(
    "Response format for grounding data. 'agent' (default): full JSON groundingReport. " +
    "'human': compressed summary string appended to the capture summary."
  ),
});

// WP-484 S2 (§3/§7 TEN-957): inline per-item relations — each batch entry can
// name relations to EXISTING Chain entries to create right after it is
// captured, resolving TEN-957 ("batch-capture lacks inline relations").
// Sibling-item (intra-batch) references are out of scope for this slice —
// `to` must be an existing entryId already on the Chain.
const batchCaptureRelationSchema = z.object({
  to: z.string().max(200).describe("Target entry ID already on the Chain, e.g. '<PREFIX>-<n>'."),
  type: z.string().max(200).describe("Relation type, e.g. 'related_to', 'informed_by', 'governs'."),
});

export const batchCaptureSchema = z.object({
  entries: z.array(z.object({
    // FEAT-160
    collection: z.string().max(200).optional().describe("Collection slug. Optional — auto-classified via LLM when omitted."),
    name: z.string().max(500).describe("Display name"),
    description: z.string().max(20000).describe("Full context / definition"),
    entryId: entryIdSchema,
    data: z.record(z.unknown()).optional().describe("Explicit field values (e.g. urgency, status, assignee). Merged with inferred values; user-provided wins."),
    canonicalKey: z.string().max(200).optional().describe("Semantic type (e.g. 'decision', 'tension', 'work_package'). Enables work-package redirect in createEntry when collection is 'chains'."),
    sourceRef: z.string().max(2000).optional().describe("URI or path of the source document backing this entry (e.g. 'meeting-2026-03-28.md', 'import://batch-5'). Stored as top-level entry field, not in data."),
    sourceExcerpt: z.string().max(5000).optional().describe("Verbatim excerpt from the source that backs this entry's claims. Stored as top-level entry field, not in data."),
    // TEN-957 (WP-484 S2): relations to create right after this entry is captured.
    relations: z.array(batchCaptureRelationSchema).max(10).optional().describe(
      "Relations to create right after this entry is captured — each {to, type} links the NEW entry to an existing Chain entry.",
    ),
  })).min(1).max(50).describe("Array of entries to capture"),
  autoCommit: z
    .boolean()
    .optional()
    .describe(
      "If true, commits created entries immediately after linking. " +
      "If omitted, Open mode workspaces commit by default and consensus/role modes stay draft-first."
    ),
  // WP-316 S3: Preview gate — dry-run mode. Returns what would happen, no DB writes.
  preview: z.boolean().optional().describe("If true, validates all captures without writing. Returns what would happen. Default false."),
});

export const captureClassifierSchema = z.object({
  enabled: z.boolean(),
  autoRouted: z.boolean(),
  agrees: z.boolean(),
  abstained: z.boolean(),
  topConfidence: z.number(),
  confidence: z.number(),
  reasons: z.array(z.string()),
  candidates: z.array(
    z.object({
      collection: z.string(),
      signalScore: z.number().optional(),
      confidence: z.number(),
      /** WP-316 S2: required-field cost — how many fields the agent must supply for this collection. */
      requiredFieldCount: z.number().optional(),
    }),
  ),
  agentProvidedCollection: z.string().optional(),
  overrideCommand: z.string().optional(),
  classifiedBy: z.enum(["llm", "heuristic", "explicit"]).optional(),
  confidenceTier: z.enum(["high", "medium", "low"]).optional(),
  reasoning: z.string().optional(),
});

export type CaptureClassifierMetadata = z.infer<typeof captureClassifierSchema>;

type CaptureToolResult = {
  content: Array<{ type: "text"; text: string }>;
  // Required (not optional) to mirror envelope.ts's ToolResult contract (DEC-1447): every
  // response this tool returns always sets structuredContent.
  structuredContent: z.infer<typeof captureOutputSchema> | EnvelopeError;
};

type CollectionResolution = {
  resolvedCollection?: string;
  classifierMeta?: CaptureClassifierMetadata;
  earlyResult?: CaptureToolResult;
};

function trackClassifierTelemetry(params: {
  workspaceId: string;
  predictedCollection: string;
  confidence: number;
  autoRouted: boolean;
  reasonCategory: ClassifierReasonCategory;
  explicitCollectionProvided: boolean;
  outcome: "auto-routed" | "fallback";
}): void {
  const telemetry = {
    predicted_collection: params.predictedCollection,
    confidence: params.confidence,
    auto_routed: params.autoRouted,
    reason_category: params.reasonCategory,
    explicit_collection_provided: params.explicitCollectionProvided,
  };

  trackCaptureClassifierEvaluated(params.workspaceId, telemetry);
  if (params.outcome === "auto-routed") {
    trackCaptureClassifierAutoRouted(params.workspaceId, telemetry);
    return;
  }
  trackCaptureClassifierFallback(params.workspaceId, telemetry);
}

function buildClassifierUnknownResult(): CaptureToolResult {
  return {
    content: [{
      type: "text" as const,
      text:
        "I could not infer a collection confidently from this input.\n\n" +
        "Please provide `collection`, or rewrite with clearer intent (decision/problem/definition/insight/bet).",
    }],
    structuredContent: failure(
      "VALIDATION_ERROR",
      "Could not infer collection from input.",
      "Provide collection explicitly, or rewrite with clearer intent.",
      [{ tool: "collections", description: "List available collections", parameters: { action: "list" } }],
      { classifier: { enabled: true, autoRouted: false, agrees: false, abstained: true, topConfidence: 0, confidence: 0, reasons: [], candidates: [] } },
    ),
  };
}

function buildProvisionedCollectionSuggestions(
  candidates: Array<{ collection: string; signalScore?: number; confidence: number }>,
): string {
  return candidates.length
    ? candidates
      .map((c) => `- \`${c.collection}\` (${c.signalScore ?? c.confidence}% confidence)`)
      .join("\n")
    : "- No collection candidates were inferred confidently.";
}

function buildLowConfidenceResult(
  resolved: ResolvedCollection,
  classifierMeta: CaptureClassifierMetadata,
): CaptureToolResult {
  const allCandidates = [
    { collection: resolved.collection, confidence: resolved.confidence },
    ...resolved.alternatives,
  ];
  // WP-316 S2: enrich suggestions with requiredFieldCount from classifierMeta.candidates
  const candidateFieldCounts = new Map<string, number>();
  for (const c of classifierMeta.candidates) {
    if (c.requiredFieldCount !== undefined) {
      candidateFieldCounts.set(c.collection, c.requiredFieldCount);
    }
  }
  const suggestions = allCandidates
    .map((c) => {
      const fieldCost = candidateFieldCounts.get(c.collection);
      const fieldNote = fieldCost !== undefined ? `, ${fieldCost} required field${fieldCost === 1 ? "" : "s"}` : "";
      return `- \`${c.collection}\` (${c.confidence}% confidence${fieldNote})`;
    })
    .join("\n");
  const textBody =
    `Low confidence classification (${resolved.confidence}%, classified by ${resolved.classifiedBy}).\n\n` +
    `Predicted: \`${resolved.collection}\`\n` +
    `Reason: ${resolved.reasoning || "low signal"}\n\n` +
    "Choose one of these and retry with `collection`:\n" +
    `${suggestions}\n\n` +
    "Correction path: rerun with your chosen `collection`, or capture and use `entries action=move` later.";
  return {
    content: [{ type: "text" as const, text: textBody }],
    structuredContent: failure(
      "VALIDATION_ERROR",
      `Low confidence routing to '${resolved.collection}' (${resolved.confidence}%, ${resolved.classifiedBy}).`,
      "Rerun with explicit collection.",
      allCandidates.map((c) => ({
        tool: "capture",
        description: `Capture to ${c.collection}`,
        parameters: { collection: c.collection },
      })),
      { classifier: classifierMeta },
    ),
  };
}


function buildClassifierMeta(
  resolved: ResolvedCollection,
  overrides: Partial<CaptureClassifierMetadata> = {},
): CaptureClassifierMetadata {
  return {
    enabled: true,
    autoRouted: false,
    agrees: true,
    abstained: false,
    topConfidence: resolved.confidence,
    confidence: resolved.confidence,
    reasons: resolved.reasoning ? [resolved.reasoning] : [],
    candidates: [
      { collection: resolved.collection, confidence: resolved.confidence },
      ...resolved.alternatives.map((a) => ({ collection: a.collection, confidence: a.confidence })),
    ].slice(0, 3),
    classifiedBy: resolved.classifiedBy,
    confidenceTier: resolved.tier,
    reasoning: resolved.reasoning,
    ...overrides,
  };
}

/**
 * WP-316 S3 gap fix: enrich classifier candidates with requiredFieldCount from collection schema.
 * This gives agents a cost signal when choosing between low-confidence candidates.
 * Advisory — never throws, falls back to unmodified candidates.
 */
async function enrichCandidatesWithFieldCounts(meta: CaptureClassifierMetadata): Promise<void> {
  try {
    const cols = await getCollections();
    const reqBySlug = new Map(
      cols.map((c) => [c.slug, (c.fields ?? []).filter((f: { required?: boolean }) => f.required === true).length]),
    );
    meta.candidates = meta.candidates.map((c) => ({
      ...c,
      requiredFieldCount: reqBySlug.get(c.collection) ?? 0,
    }));
  } catch {
    // enrichment is advisory; never fail capture for this
  }
}

/**
 * BET-288 S0: Fire confusion-matrix telemetry on every classification.
 * Called from every code path that produces a ResolvedCollection.
 */
function emitClassificationTelemetry(
  resolved: ResolvedCollection,
  workspaceId: string,
  opts: { explicitCollectionProvided: boolean; autoRouted: boolean },
): void {
  const topAlt = resolved.alternatives?.[0] ?? null;
  trackCollectionClassified(workspaceId, {
    collection_slug: resolved.collection,
    thinking_layer: resolved.thinkingLayer ?? null,
    confidence: resolved.confidence,
    classified_by: resolved.classifiedBy,
    confidence_tier: resolved.tier,
    alternative_slug: topAlt?.collection ?? null,
    alternative_confidence: topAlt?.confidence ?? null,
    explicit_collection_provided: opts.explicitCollectionProvided,
    auto_routed: opts.autoRouted,
  });
}

async function resolveCaptureCollection(params: {
  collection?: string;
  name: string;
  description: string;
  workspaceId: string;
  explicitCollectionProvided: boolean;
}): Promise<CollectionResolution> {
  const {
    collection,
    name,
    description,
    workspaceId,
    explicitCollectionProvided,
  } = params;

  if (collection) {
    const resolved = await resolveCollection({ name, description });
    if (resolved) {
      const agrees = resolved.collection === collection;
      const classifierMeta = buildClassifierMeta(resolved, {
        autoRouted: false,
        agrees,
        agentProvidedCollection: collection,
        ...(!agrees && {
          overrideCommand: `capture collection="${resolved.collection}"`,
        }),
      });
      await enrichCandidatesWithFieldCounts(classifierMeta);
      // BET-288 S0: Fire confusion telemetry on every classification (explicit path)
      emitClassificationTelemetry(resolved, workspaceId, {
        explicitCollectionProvided: true,
        autoRouted: false,
      });
      if (!agrees) {
        trackClassifierTelemetry({
          workspaceId,
          predictedCollection: resolved.collection,
          confidence: resolved.confidence,
          autoRouted: false,
          reasonCategory: "low-confidence",
          explicitCollectionProvided: true,
          outcome: "fallback",
        });
        // WP-316 S2: track divergence — agent overrode classifier suggestion
        const agentInCandidates =
          resolved.alternatives.some((a) => a.collection === collection) ||
          resolved.collection === collection;
        trackClassifierDivergence(workspaceId, {
          classifier_collection: resolved.collection,
          agent_collection: collection,
          classifier_confidence: resolved.confidence,
          classifier_tier: resolved.tier,
          agent_in_candidates: agentInCandidates,
        });
      }
      return { resolvedCollection: collection, classifierMeta };
    }
    return {
      resolvedCollection: collection,
      classifierMeta: {
        enabled: true,
        autoRouted: false,
        agrees: false,
        abstained: true,
        topConfidence: 0,
        confidence: 0,
        reasons: [],
        candidates: [],
        agentProvidedCollection: collection,
      },
    };
  }

  const resolved = await resolveCollection({ name, description });
  if (!resolved) {
    trackClassifierTelemetry({
      workspaceId,
      predictedCollection: "unknown",
      confidence: 0,
      autoRouted: false,
      reasonCategory: "low-confidence",
      explicitCollectionProvided,
      outcome: "fallback",
    });
    return { earlyResult: buildClassifierUnknownResult() };
  }

  // FEAT-159: Tier-based routing replaces shouldAutoRouteClassification/isClassificationAmbiguous.
  // High+medium auto-route; low rejects and asks for explicit collection.
  const autoRoute = resolved.tier !== "low";
  const classifierMeta = buildClassifierMeta(resolved, { autoRouted: autoRoute });
  await enrichCandidatesWithFieldCounts(classifierMeta);

  // BET-288 S0: Fire confusion telemetry on every classification (classifier path)
  emitClassificationTelemetry(resolved, workspaceId, {
    explicitCollectionProvided,
    autoRouted: autoRoute,
  });

  if (!autoRoute) {
    trackClassifierTelemetry({
      workspaceId,
      predictedCollection: resolved.collection,
      confidence: resolved.confidence,
      autoRouted: false,
      reasonCategory: "low-confidence",
      explicitCollectionProvided,
      outcome: "fallback",
    });
    return {
      classifierMeta,
      earlyResult: buildLowConfidenceResult(resolved, classifierMeta),
    };
  }

  trackClassifierTelemetry({
    workspaceId,
    predictedCollection: resolved.collection,
    confidence: resolved.confidence,
    autoRouted: true,
    reasonCategory: "auto-routed",
    explicitCollectionProvided,
    outcome: "auto-routed",
  });

  return {
    resolvedCollection: resolved.collection,
    classifierMeta,
  };
}

const captureSuccessOutputSchema = z.object({
  entryId: z.string(),
  collection: z.string(),
  name: z.string(),
  // "draft_on_failure" is the status emitted when an auto-commit is REFUSED/failed (the entry
  // stays a draft) — the same path that now also emits coherencyRefusal. The strict schema must
  // accept it or a refused response fails to validate on `status` (codex review, completing the
  // coherencyRefusal contract fix).
  status: z.enum(["draft", "committed", "proposed", "draft_on_failure"]),
  // WP-480 S1: `qualityScore` (required, client N/10) deleted — a named, accepted
  // breaking output-contract change. The server verdict stays as `qualityVerdict`.
  qualityVerdict: z.record(z.unknown()).optional(),
  classifier: captureClassifierSchema.optional(),
  studioUrl: z.string().optional(),
  warnings: z.array(z.string()).optional(),
  normalization: z.object({
    remapped: z.record(z.string()).optional(),
    rejected: z.array(z.string()).optional(),
  }).optional(),
  expectedFields: z.array(z.object({
    key: z.string(),
    type: z.string(),
    required: z.boolean().optional(),
  })).optional(),
  // TEN-2365: capture-time authority-domain proposal (PENDING ratification), when one was filed.
  authorityDomain: z.object({
    slug: z.string(),
    status: z.literal("proposal-pending"),
  }).optional(),
  // WP-465 surface parity: structured coherency refusal when an auto-commit is gate-refused.
  // Loose record (matching the qualityVerdict convention above) — the CoherencyRefusal contract
  // is authored in convex/lib/gates/coherencyControls.ts; re-declaring its shape here would create
  // a second source of truth. Required because the schema is `.strict()` (an undeclared key throws).
  coherencyRefusal: z.record(z.unknown()).optional(),
}).strict();

const captureClassifierOnlyOutputSchema = z.object({
  classifier: captureClassifierSchema,
}).strict();

export const captureOutputSchema = z.union([
  captureSuccessOutputSchema,
  captureClassifierOnlyOutputSchema,
]);

export const batchCaptureOutputSchema = z.object({
  captured: z.array(z.object({
    entryId: z.string(),
    collection: z.string(),
    name: z.string(),
    // "draft_on_failure" is the status emitted when an auto-commit is REFUSED/failed (the entry
  // stays a draft) — the same path that now also emits coherencyRefusal. The strict schema must
  // accept it or a refused response fails to validate on `status` (codex review, completing the
  // coherencyRefusal contract fix).
  status: z.enum(["draft", "committed", "proposed", "draft_on_failure"]),
    classifiedBy: z.enum(["llm", "heuristic", "explicit"]).optional(),
    confidence: z.number().optional(),
    confidenceTier: z.enum(["high", "medium", "low"]).optional(),
    warnings: z.array(z.string()).optional(),
    normalization: z.object({
      remapped: z.record(z.string()).optional(),
      rejected: z.array(z.string()).optional(),
    }).optional(),
    // TEN-2365: capture-time authority-domain proposal slug (PENDING ratification), when filed.
    domain: z.string().optional(),
    // WP-465 surface parity: structured coherency refusal for a gate-refused auto-commit (batch
    // shape). Loose record — same single-source rationale as captureSuccessOutputSchema above.
    coherencyRefusal: z.record(z.unknown()).optional(),
  })),
  total: z.number(),
  failed: z.number(),
  committed: z.number(),
  proposed: z.number(),
  drafts: z.number(),
  classified: z.number().optional(),
  autoCommitApplied: z.boolean(),
  skippedLowConfidence: z.array(z.object({
    index: z.number(),
    name: z.string(),
    suggestedCollection: z.string().optional(),
    confidence: z.number().optional(),
    alternatives: z.array(z.object({
      collection: z.string(),
      confidence: z.number(),
    })).optional(),
  })).optional(),
  failedEntries: z.array(z.object({
    index: z.number(),
    collection: z.string(),
    name: z.string(),
    error: z.string(),
  })).optional(),
});

/**
 * TEN-194: Auto-commit only in Open mode. In consensus/role, draft-first always —
 * ignore autoCommit: true to prevent governance bypass.
 */
function shouldAutoCommitCapture(
  autoCommit: boolean | undefined,
  governanceMode: string | undefined,
): boolean {
  if (governanceMode !== "open") return false;
  return autoCommit === true || autoCommit === undefined;
}

/**
 * Build initial entry data from collection field definitions using type-aware defaults.
 * Single source of truth: FIELD_TYPE_DEFAULTS from fieldTypes.ts (BET-106, DEC-174).
 * Deduplicates logic previously inlined in both capture and batch-capture (TEN-305).
 */
function buildDataFromFields(
  fields: Array<{ key: string; type: string; [k: string]: unknown }>,
  descriptionField: string,
  descriptionValue: string,
): Record<string, unknown> {
  const data: Record<string, unknown> = {};
  for (const field of fields) {
    if (field.key === descriptionField) {
      data[field.key] = descriptionValue;
    } else {
      data[field.key] = FIELD_TYPE_DEFAULTS[field.type as FieldType] ?? null;
    }
  }
  return data;
}

/** Returns true if a value should be considered empty (null, empty array, or blank string). */
function isEmptyValue(v: unknown): boolean {
  return v == null || (Array.isArray(v) ? v.length === 0 : String(v).trim() === "");
}

/** Returns true if a value should be considered non-empty (opposite of isEmptyValue). */
function isNonEmptyValue(v: unknown): boolean {
  return v != null && (Array.isArray(v) ? v.length > 0 : String(v).trim() !== "");
}

/** Levenshtein distance for fuzzy select matching. DEC-173: fix when possible, never silently accept. */
function levenshtein(a: string, b: string): number {
  const m = a.length;
  const n = b.length;
  const dp: number[][] = Array(m + 1)
    .fill(null)
    .map(() => Array(n + 1).fill(0));
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      dp[i][j] = Math.min(dp[i - 1][j] + 1, dp[i][j - 1] + 1, dp[i - 1][j - 1] + cost);
    }
  }
  return dp[m][n];
}

const APPETITE_OPTIONS = ["small", "medium", "large"] as const;

function findClosestOption(value: string, options: readonly string[]): { opt: string; dist: number } {
  const lower = value.toLowerCase();
  const exact = options.find((o) => o.toLowerCase() === lower);
  if (exact) return { opt: exact, dist: 0 };
  return options.reduce(
    (acc, opt) => {
      const d = levenshtein(lower, opt.toLowerCase());
      return d < acc.dist ? { opt, dist: d } : acc;
    },
    { opt: options[0], dist: Infinity },
  );
}

/**
 * Canonicalize select fields: if current value is not in options, find closest match.
 * Case-insensitive exact first, then Levenshtein distance. DEC-173: fix when possible.
 * BET-125 Slice 1: appetite (top-level before bet move), horizon, severity, priority from col.fields.
 */
function canonicalizeSelects(
  data: Record<string, unknown>,
  fields: Array<{ key: string; type: string; options?: string[] }>,
  isBet: boolean,
): void {
  if (isBet && data.appetite != null && typeof data.appetite === "string") {
    const val = data.appetite.trim();
    if (val) {
      const { opt, dist } = findClosestOption(val, APPETITE_OPTIONS);
      if (dist <= 4) data.appetite = opt;
    }
  }

  for (const field of fields) {
    if (field.type !== "select" || !field.options?.length) continue;
    if (field.key === "appetite" && isBet) continue;
    const val = data[field.key];
    if (val == null || typeof val !== "string") continue;
    const trimmed = val.trim();
    if (!trimmed) continue;
    const { opt, dist } = findClosestOption(trimmed, field.options);
    if (dist <= 4) data[field.key] = opt;
  }
}

// WP-484 S2 (§3): `capture` absorbs `batch-capture` → action=batch, `items[]`
// (each item may carry inline `relations` — TEN-957). Legacy `batch-capture`
// tool stays registered alongside (§7 "alongside" mechanic) — both call the
// SAME handleCapture/handleBatchCapture functions, one business-logic path.
const CAPTURE_ACTIONS = ["capture", "batch"] as const;
type CaptureAction = (typeof CAPTURE_ACTIONS)[number];

const captureItemSchema = batchCaptureSchema.shape.entries.element;

// §3a — the REGISTERED layer: flat ZodObject, action enum + superset of every
// absorbed action's params (single-capture fields relaxed to optional; batch
// adds `items`). `action` defaults to 'capture' — `capture`'s name is REUSED
// (not "alongside"-eligible), and its pre-S2 behavior took no `action` param,
// so a default preserves every existing bare single-entry capture call site
// until the DEC-283 sweep (S3).
export const captureCompoundSchema = z.object({
  action: z.enum(CAPTURE_ACTIONS).optional().default("capture").describe(
    "'capture': create a single knowledge entry (the original capture behavior). " +
      "'batch': create multiple entries in one call via `items[]` (absorbs batch-capture; each item may carry inline `relations`).",
  ),
  // For 'capture' — mirrors captureSchema, relaxed to optional (batch omits these).
  collection: z.string().max(200).optional().describe("For 'capture': collection slug, e.g. 'tensions', 'business-rules', 'glossary', 'decisions'. Optional — classifier auto-routes when omitted."),
  name: z.string().max(500).optional().describe("For 'capture': display name — required for this action."),
  description: z.string().max(20000).optional().describe("For 'capture': full context — required for this action."),
  context: z.string().max(5000).optional().describe("For 'capture': optional additional context."),
  entryId: entryIdSchema,
  canonicalKey: z.string().max(200).optional().describe("For 'capture': semantic type (e.g. 'decision', 'tension', 'vision'). Auto-assigned from collection if omitted."),
  data: z.record(z.unknown()).optional().describe("For 'capture': explicit field values. Merged with inferred values; user-provided wins."),
  links: z.array(z.object({
    to: z.string().max(200).describe("Target entry ID (e.g. '<PREFIX>-<n>')"),
    type: z.string().max(200).describe("Relation type (e.g. 'governs', 'related_to', 'informs')"),
  })).max(20).optional().describe("For 'capture': relations to create after capture. Skips auto-link discovery when provided."),
  autoCommit: z.boolean().optional().describe("For 'capture'/'batch': if true, commits immediately after capture + linking."),
  sourceRef: z.string().max(2000).optional().describe("For 'capture': URI or path of the source document backing this entry."),
  sourceExcerpt: z.string().max(5000).optional().describe("For 'capture': verbatim excerpt from the source backing this entry's claims."),
  preview: z.boolean().optional().describe("For 'capture'/'batch': if true, validates without writing."),
  suggestOnly: z.boolean().optional().describe("For 'capture': if true, runs pre-write grounding and returns a groundingReport WITHOUT creating any entry."),
  format: z.enum(["agent", "human"]).optional().describe("For 'capture': response format for grounding data."),
  // For 'batch' — mirrors batchCaptureSchema's `entries`, renamed `items` per §3.
  items: z.array(captureItemSchema).min(1).max(50).optional().describe("For 'batch': array of entries to capture, each may carry inline `relations`."),
});

// §3b — the HANDLER layer: per-action discriminatedUnion, reusing the
// existing single-purpose schemas (Code Integrity — captureSchema/
// batchCaptureSchema already know these shapes).
const captureSingleVariant = captureSchema.extend({ action: z.literal("capture") });
const captureBatchVariant = z.object({
  action: z.literal("batch"),
  items: z.array(captureItemSchema).min(1).max(50),
  autoCommit: z.boolean().optional(),
  preview: z.boolean().optional(),
});

export const captureActionUnion = z.discriminatedUnion("action", [
  captureSingleVariant,
  captureBatchVariant,
]);

/** §4 backpressure text — one entry per action, never used for registration. */
export const CAPTURE_ACTION_SPECS: Record<CaptureAction, ActionParamSpec> = {
  capture: {
    params: ["collection", "name", "description", "context", "entryId", "canonicalKey", "data", "links", "autoCommit", "sourceRef", "sourceExcerpt", "preview", "suggestOnly", "format"],
    description: "name and description are required.",
  },
  batch: { params: ["items", "autoCommit", "preview"], description: "items (1-50, each may carry inline relations) is required." },
};

export function registerSmartCaptureTools(server: McpServer) {
  // §3b handler map — dispatched via dispatchDiscriminated.
  const captureHandlers: Record<CaptureAction, (data: any) => Promise<ToolResult>> = {
    capture: (data) => handleCapture(server, data),
    batch: (data) => handleBatchCapture(server, { entries: data.items, autoCommit: data.autoCommit, preview: data.preview }),
  };

  const captureTool = server.registerTool(
    "capture",
    {
      title: "Capture",
      description:
        "The tool for creating knowledge entries — single or batch. Two actions:\n\n" +
        "- **capture**: Create a single entry. Creates an entry, auto-links related entries, and returns a quality " +
        "scorecard — all in one call. Provide a name and description; `collection` is optional — the classifier " +
        "auto-routes when omitted.\n" +
        "- **batch**: Create multiple entries in one call via `items[]` (absorbs batch-capture). Each item may carry " +
        "inline `relations` created right after that entry is captured.\n\n" +
        "**action=capture details:**\n" +
        "Supported collections with smart profiles: tensions, business-rules, glossary, decisions, features, " +
        "audiences, strategy, standards, maps, chains, insights, assumptions, principles, tracking-events.\n" +
        "All other collections get an ENT-{random} ID and sensible defaults.\n\n" +
        "**Explicit data:** When you know the schema, pass `data: { field: value }` to set fields directly. " +
        "Top-level `name` and `description` always win for those fields. `data` wins over inference for all other fields.\n\n" +
        "**Compound capture:** Pass `links` to create relations in the same call (skips auto-link discovery). " +
        "In Open mode, entries commit by default when `autoCommit` is omitted. " +
        "Pass `autoCommit: true` to promote the entry from draft to SSOT immediately after linking when you want to be explicit. " +
        "Governed collections (glossary, business-rules, principles, standards, strategy, features, architecture) " +
        "will warn but still commit — use only when you're certain.\n\n" +
        "Entries are created as `draft` first, then may publish immediately depending on governance and `autoCommit`. Use `entries action=update` for post-creation adjustments.",
      inputSchema: captureCompoundSchema,
      annotations: { readOnlyHint: false, destructiveHint: false, openWorldHint: false },
    },
    thinWrapper(async (args) => {
      const parsed = parseOrFail(captureCompoundSchema, args);
      if (!parsed.ok) return parsed.result;
      const { action } = parsed.data;

      return runWithToolContext({ tool: "capture", action }, () =>
        dispatchDiscriminated("capture", captureActionUnion, parsed.data, CAPTURE_ACTION_SPECS, captureHandlers),
      );
    }),
  );
  trackWriteTool(captureTool);

  // WP-484 S3 (clean break, TEN-2157): the standalone `batch-capture` tool
  // registration retired here (registerBatchCaptureTool deleted below).
  // handleBatchCapture stays live via `capture action=batch`.
}

/**
 * WP-484 S2 (§3): extracted single-item capture handler — reused by the
 * legacy `capture` standalone tool AND the new `capture action=capture`
 * (unchanged shape; the noun itself absorbs `batch-capture` as `action=batch`,
 * §3 core-nouns table). One business-logic path.
 */
export async function handleCapture(
  server: McpServer,
  { collection, name, description, context, entryId, canonicalKey, data: userData, links, autoCommit, sourceRef, sourceExcerpt, preview, suggestOnly, format }: z.infer<typeof captureSchema>,
) {
      requireWriteAccess();

      const timingStart = Date.now();

      const wsCtx = await getWorkspaceContext();
      const explicitCollectionProvided = typeof collection === "string" && collection.trim().length > 0;

      const resolution = await resolveCaptureCollection({
        collection,
        name,
        description,
        workspaceId: wsCtx.workspaceId,
        explicitCollectionProvided,
      });
      if (resolution.earlyResult) {
        return resolution.earlyResult;
      }
      let resolvedCollection = resolution.resolvedCollection;
      const classifierMeta = resolution.classifierMeta;
      if (!resolvedCollection) {
        return buildClassifierUnknownResult();
      }

      // DEC-313 GAP 1: When classifier auto-routes to 'chains' and candidates
      // include 'work-packages', inject canonicalKey='work_package' so createEntry's isBetSignal
      // guard can redirect chains→work-packages. Only fires for classifier-driven routing
      // (not explicit agent choice) and when no canonicalKey was already provided.
      if (
        resolvedCollection === 'chains' &&
        !canonicalKey &&
        !explicitCollectionProvided &&
        classifierMeta?.candidates?.some(
          (c: { collection: string }) => c.collection === 'work-packages'
        )
      ) {
        canonicalKey = 'work_package';
      }

      const tAfterClassify = Date.now();

      // BR-111: entryId only allowed for business-rules and standards
      if (entryId && resolvedCollection !== "business-rules" && resolvedCollection !== "standards") {
        return {
          content: [{
            type: "text" as const,
            // BR-111
            text: `# entryId Not Allowed\n\n\`entryId\` is only valid for \`business-rules\` and \`standards\` collections. For \`${resolvedCollection}\`, omit \`entryId\` — IDs are auto-generated.`,
          }],
          structuredContent: failure(
            "INVALID_ENTRY_ID",
            `entryId is only allowed for business-rules and standards. Omit for ${resolvedCollection}.`,
            "Remove entryId from your capture call.",
            [],
          ),
        };
      }

      const profile = await getProfile(resolvedCollection);

      const col = await kernelQuery<CollectionDoc | null>("chain.getCollection", { slug: resolvedCollection });
      if (!col) {
        const displayName = resolvedCollection.split("-").map((w: string) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
        return {
          content: [{
            type: "text" as const,
            text:
              `Collection \`${resolvedCollection}\` not found.\n\n` +
              `**To create it**, run:\n` +
              `\`\`\`\ncollections action=create slug="${resolvedCollection}" name="${displayName}" description="..."\n\`\`\`\n\n` +
              `Or use \`collections action=list\` to see available collections.`,
          }],
          structuredContent: failure(
            "NOT_FOUND",
            `Collection '${resolvedCollection}' not found.`,
            "Create the collection first, or use collections action=list to see available ones.",
            [
              { tool: "collections", description: "Create collection", parameters: { action: "create", slug: resolvedCollection, name: displayName } },
              { tool: "collections", description: "List collections", parameters: { action: "list" } },
            ],
          ),
        };
      }

      // WP-318 S2 E1: Pre-write grounding — call suggestLinksForCapture in parallel with collection fetch.
      // Graceful degradation: grounding NEVER blocks capture. Any error → null.
      // Only runs when there's searchable content and auto-discovery is not skipped.
      const skipAutoDiscoveryEarly = links && links.length > 0;
      const groundingStart = Date.now();
      const groundingPromise = (!skipAutoDiscoveryEarly && (name || description))
        ? kernelQuery<any[]>("chain.suggestLinksForCapture", {
            entries: [{ name, description: description ?? "", collectionHint: resolvedCollection }],
            limit: 5,
            threshold: 2,
          }).catch(() => null)
        : Promise.resolve(null);

      // WP-318 S2 E1: Await grounding result — capped at 500ms to preserve fail-open on latency.
      const groundingRaw = await Promise.race([
        groundingPromise,
        new Promise<null>((r) => setTimeout(() => r(null), 500)),
      ]);
      const groundingElapsedMs = Date.now() - groundingStart;
      const groundingEntry = (groundingRaw as any)?.[0] ?? null;
      const groundingRelated: Array<{
        entryId: string; name: string; collectionSlug: string;
        overlapRatio: number; recommendedRelationType: string; reasoning: string;
      }> = groundingEntry?.related ?? [];
      const groundingDuplicates: Array<{
        entryId: string; name: string; collectionSlug: string;
        matchType: "related" | "possible_duplicate"; overlapRatio: number;
      }> = groundingEntry?.duplicates ?? [];

      // WP-319 S2: Consume governance[] from query response (moved from local isGoverned loop).
      // governance[] is pre-computed by suggestLinksForCapture (WP-319 S1) — no async round trips needed.
      // Severity field from the query is ignored here; MCP banner display does not distinguish severity yet.
      const groundingGovernance: Array<{ entryId: string; name: string; collectionSlug: string }> =
        (groundingEntry?.governance ?? []).map((g: any) => ({
          entryId: g.entryId,
          name: g.name,
          collectionSlug: g.collectionSlug,
        }));

      const groundingReport = {
        related: groundingRelated.map((r) => ({
          entryId: r.entryId,
          name: r.name,
          collectionSlug: r.collectionSlug,
          overlapRatio: r.overlapRatio,
          recommendedRelationType: r.recommendedRelationType,
          reasoning: r.reasoning,
        })),
        duplicates: groundingDuplicates.map((d) => ({
          entryId: d.entryId,
          name: d.name,
          collectionSlug: d.collectionSlug,
          matchType: d.matchType,
          overlapRatio: d.overlapRatio,
        })),
        governance: groundingGovernance,
      };

      // WP-318 S2 E3: suggestOnly — return grounding preview without creating entry
      if (suggestOnly) {
        const hasSuggestions = groundingReport.related.length > 0 || groundingReport.duplicates.length > 0;
        const summaryText = hasSuggestions
          ? `Grounding preview for "${name}": ${groundingReport.related.length} related, ${groundingReport.duplicates.length} possible duplicates. No entry created.`
          : `Grounding preview for "${name}": no similar entries found. Safe to capture.`;
        return {
          content: [{
            type: "text" as const,
            text: `# Grounding Preview — No Entry Created\n\n${summaryText}\n\n` +
              (groundingReport.related.length > 0
                ? `## Related entries\n${groundingReport.related.map((r) => `- **${r.entryId}** ${r.name} [${r.collectionSlug}] — ${r.reasoning}`).join("\n")}\n\n`
                : "") +
              (groundingReport.duplicates.length > 0
                ? `## Possible duplicates\n${groundingReport.duplicates.map((d) => `- **${d.entryId}** ${d.name} [${d.collectionSlug}] (${d.matchType}, overlap: ${d.overlapRatio})`).join("\n")}\n\n`
                : "") +
              (groundingReport.governance.length > 0
                ? `## Governance entries to review\n${groundingReport.governance.map((g) => `- **${g.entryId}** ${g.name} [${g.collectionSlug}]`).join("\n")}\n\n`
                : "") +
              `_Call \`capture\` without \`suggestOnly\` to proceed with entry creation._`,
          }],
          structuredContent: {
            ...success(
              summaryText,
              { groundingReport, outcome: "suggest_only" as const },
              [{ tool: "capture", description: "Capture for real", parameters: { collection: resolvedCollection, name, description } }],
            ),
          },
        };
      }

      // 2. Build data with profile defaults + inference
      const data = buildDataFromFields(col.fields ?? [], profile.descriptionField, description);

      for (const def of profile.defaults) {
        if (def.value !== "infer" && def.value !== "today") {
          data[def.key] = def.value;
        }
      }

      if (profile.inferField) {
        const inferred = profile.inferField({
          collection: resolvedCollection, name, description, context, data, entryId: "",
          linksCreated: [], linksSuggested: [], collectionFields: col.fields ?? [],
        });
        for (const [key, val] of Object.entries(inferred)) {
          if (val !== undefined && val !== "") {
            data[key] = val;
          }
        }
      }

      if (isEmptyValue(data.date)) {
        const inferredSourceDate = inferSourceDate(name, description, context, sourceRef, sourceExcerpt);
        if (inferredSourceDate && (col.fields ?? []).some((field) => field.key === "date")) {
          data.date = inferredSourceDate;
        }
      }

      // Merge user-provided data (wins over inference for those fields)
      if (userData && typeof userData === "object") {
        for (const [key, val] of Object.entries(userData)) {
          if (key !== "name") data[key] = val;
        }
      }

      // Top-level description always wins
      data[profile.descriptionField || "description"] = description;

      // BET-281: Identify bet captures via canonicalKey (primary) or collection's defaultCanonicalKey.
      const colMeta = await getCollectionBySlug(resolvedCollection);
      const isBetCapture = canonicalKey === "work_package" || colMeta?.defaultCanonicalKey === "work_package";

      // BET-125: decomposeContent (strategy goals/rationale/scope; bet problem/elements/doneWhen) + canonicalizeSelects
      let entryWarnings: string[] = [];
      // BET-288: derive strategy check from thinkingLayer instead of hardcoded slug
      const isStrategyCapture = colMeta?.thinkingLayer === "strategy";
      const shouldDecompose =
        (isStrategyCapture || isBetCapture) &&
        description.trim().length > 0;
      if (shouldDecompose) {
        try {
          const decomposed = await kernelCall<{ decomposed: Record<string, unknown> } | null>(
            "chain.decomposeContent",
            {
              collectionSlug: resolvedCollection,
              entryName: name,
              description,
              existingData: data,
              ...(isBetCapture ? { canonicalKey: "work_package" } : {}),
              ...(colMeta?.thinkingLayer ? { thinkingLayer: colMeta.thinkingLayer } : {}),
            },
          );
          if (decomposed?.decomposed) {
            for (const [key, val] of Object.entries(decomposed.decomposed)) {
              if (isEmptyValue(data[key]) && isNonEmptyValue(val)) {
                data[key] = val;
              }
            }
          }
        } catch {
          // Non-blocking: capture proceeds without decomposition (BET-125 contract)
          entryWarnings.push("Content decomposition skipped (transient error). Entry saved with description only.");
        }
      }
      canonicalizeSelects(data, col.fields ?? [], isBetCapture);

      // BET-132: bet captures keep flat fields (problem, appetite, etc.) — no data.links wrapping

      // 3b. Status is now resolved server-side (DEC-896 / BR-76): Open mode → 'active', else 'draft'.
      // Do NOT pass explicit status here so governance mode governs. Callers that need draft can pass it.
      const agentId = getAgentSessionId();

      // 4. Create entry (Convex generates sequential ID when entryId not provided)
      let finalEntryId: string;
      let internalId: string;
      let normalizationMeta: { remapped: Record<string, string>; rejected: string[]; accepted: string[] } | undefined;
      // BET-272 S3: Formative quality hints — populated from createEntryWithClassification result (STD-101)
      let formativeHints: Array<{ field: string; hint: string; severity: "advisory" }> = [];
      // BET-272 S5: Advisory relation suggestions from graph (BR-144, STD-147)
      let relationSuggestionsFromCreate: Array<{ targetEntryId: string; targetName: string; relationType: string; confidence: number; reasoning: string }> = [];
      // TEN-2365: capture-time authority-domain proposal echo (PENDING ratification), when filed.
      let authorityDomainFromCreate: { slug: string; status: "proposal-pending" } | undefined;
      // WP-475 E1: heuristic coaching verdict delivered in the createEntry response.
      // `undefined` = field absent (older deploy) → fall back to the explicit call below.
      let coachingFromCreate: CoachingResultSubset | null | undefined;
      // DEC-896 / BR-76 S3: server returned 'active' — skip post-write commitEntry call.
      let wasAutoCommittedServerSide = false;
      try {
        const result = await kernelMutation<{
          preview?: boolean;
          /** Phase 2 (D1): preview predicts the governance WHY hard-gate — true when the real capture would be rejected. */
          wouldBlock?: boolean;
          blockingReason?: string;
          docId: string; entryId: string;
          /** DEC-896 / BR-76: 'active' means server auto-committed (Open mode). Guard skips commitEntry. */
          status?: "draft" | "active" | "deprecated";
          warnings?: string[];
          normalization?: { remapped: Record<string, string>; rejected: string[]; accepted: string[] };
          contract?: CaptureContract;
          // BET-272 S5: Advisory relation suggestions from graph (BR-144, STD-147)
          relationSuggestions?: Array<{ targetEntryId: string; targetName: string; relationType: string; confidence: number; reasoning: string }>;
          // BET-272 S3: Advisory quality hints from collection qualityCriteria (BR-144)
          qualityHints?: Array<{ field: string; hint: string; severity: "advisory" }>;
          // TEN-2365: capture-time authority-domain proposal (PENDING ratification), when filed.
          authorityDomain?: { slug: string; status: "proposal-pending" };
          // WP-475 E1: heuristic coaching verdict delivered in the write response (BR-144).
          coaching?: CoachingResultSubset | null;
          // TEN-2458: capture-preview task-alignment verdict (slice ③, advisory-only until slice ④).
          taskAlignment?: {
            verdict: string;
            disposition: string;
            coldStart: boolean;
            matchedEntry: { entryId: string; name: string } | null;
            matchSource: string | null;
            activeWorkPackageCount: number;
            candidates: Array<{ entryId: string; name: string }>;
            escapeRoutes: Array<{ kind: string; instruction: string }>;
            citedRule: { entryId: string; name: string } | null;
            wording: string;
          };
        }>("chain.createEntry", {
          collectionSlug: resolvedCollection,
          entryId: entryId ?? undefined,
          name,
          // DEC-896 / BR-76: omit status — server resolves from governanceMode (Open→active, else draft).
          data,
          canonicalKey,
          createdBy: agentId ? `agent:${agentId}` : "capture",
          sessionId: agentId ?? undefined,
          ...(sourceRef ? { sourceRef } : {}),
          ...(sourceExcerpt ? { sourceExcerpt } : {}),
          ...(preview ? { preview: true } : {}),
          // WP-475 E1 (Codex P2 — CLI parity): user links are known UPFRONT, so the
          // post-relations re-evaluation below owns the single LLM run — defer the
          // create-time LLM schedule (deterministic, mirrors `pb capture --link`).
          // Auto-discovered links are unknown here: zero-user-link captures do NOT
          // defer (a link-less capture must keep its LLM run); the supersession
          // guard bounds the auto-link double-run race when timing allows.
          // Fail-safe: if the relations batch then creates nothing, the deferred row
          // keeps its delivered heuristic verdict with llmStatus 'skipped' (same
          // accepted shape as the CLI dying between create and re-eval).
          ...(links && links.length > 0 ? { deferLLMSchedule: true } : {}),
        });

        // WP-316 S3: Preview gate — return dry-run result without recording session activity.
        if (result?.preview) {
          const hasGrounding = groundingReport.related.length > 0 || groundingReport.duplicates.length > 0 || groundingReport.governance.length > 0;
          const groundingSummary = hasGrounding
            ? `\n\n**Grounding:** ${[
                groundingReport.duplicates.length > 0 && `${groundingReport.duplicates.length} possible duplicate${groundingReport.duplicates.length > 1 ? "s" : ""}`,
                groundingReport.related.length > 0 && `${groundingReport.related.length} related entr${groundingReport.related.length > 1 ? "ies" : "y"}`,
                groundingReport.governance.length > 0 && `${groundingReport.governance[0]?.entryId} may govern this`,
              ].filter(Boolean).join(", ")}.`
            : "";
          // Phase 2 (D1): when the server predicts the governance WHY hard-gate would
          // reject this capture, the preview must say "would be BLOCKED" — not "would
          // capture" — so the dry-run contract stays honest for the newly gated inputs.
          const wouldBlock = result.wouldBlock === true;
          const headline = wouldBlock
            ? `Preview: would be BLOCKED — "${name}" [${resolvedCollection}]`
            : `Preview: would capture "${name}" [${resolvedCollection}]`;
          const previewEnvelope = success(
            wouldBlock
              ? `Preview: would be BLOCKED — "${name}" — no DB writes`
              : `Preview: would capture "${name}" — no DB writes`,
            {
              entryId: result.entryId,
              name,
              collection: resolvedCollection,
              outcome: wouldBlock ? ("blocked" as const) : ("preview" as const),
              ...(wouldBlock && result.blockingReason ? { blockingReason: result.blockingReason } : {}),
              warnings: result.warnings ?? [],
              groundingReport,
              // TEN-2458: thread task-alignment verdict when kernel computed it for this preview.
              ...(result.taskAlignment ? { taskAlignment: result.taskAlignment } : {}),
            },
            [{ tool: "capture", description: "Capture for real", parameters: { collection: resolvedCollection, name, description } }],
          );
          if (result.contract) {
            previewEnvelope.contract = result.contract;
          }
          const taskAlignmentNote = result.taskAlignment
            ? `\n\n**Task alignment:** ${result.taskAlignment.wording}`
            : "";
          return {
            content: [{ type: "text" as const, text: `# ${headline}\n\nNo DB writes — call without \`preview:true\` to capture for real.${result.warnings?.length ? `\n\n**Warnings:** ${result.warnings.join("; ")}` : ""}${groundingSummary}${taskAlignmentNote}` }],
            structuredContent: previewEnvelope,
          };
        }
        internalId = result.docId;
        finalEntryId = result.entryId;
        entryWarnings.push(...(result.warnings ?? []));
        normalizationMeta = result.normalization;
        // BET-272 S5: Advisory relation suggestions from graph (BR-144, STD-147)
        relationSuggestionsFromCreate = result.relationSuggestions ?? [];
        // BET-272 S3: Quality hints from createEntryWithClassification (STD-101: single source)
        formativeHints = result.qualityHints ?? [];
        // TEN-2365: capture-time authority-domain proposal echo.
        authorityDomainFromCreate = result.authorityDomain;
        // WP-475 E1: consume the inline coaching verdict (undefined = older deploy).
        coachingFromCreate = result.coaching;
        resolveGapsForEntry(name, result.entryId);
        // DEC-896 / BR-76 S3: server already committed (Open mode auto-commit). Mark and skip commitEntry.
        if (result.status === "active") {
          wasAutoCommittedServerSide = true;
        }
      } catch (error: unknown) {
        const msg = error instanceof Error ? error.message : String(error);
        if (msg.includes("Duplicate") || msg.includes("already exists")) {
          return {
            content: [{
              type: "text" as const,
              text: `# Cannot Capture — Duplicate Detected\n\n${msg}\n\nUse \`entries action=get\` to inspect the existing entry, or \`entries action=update\` to modify it.`,
            }],
            structuredContent: failure(
              "DUPLICATE",
              msg,
              "Use entries action=get to inspect the existing entry.",
              [
                { tool: "entries", description: "Get existing entry", parameters: { action: "get", entryId: entryId ?? name } },
                { tool: "entries", description: "Update existing entry", parameters: { action: "update", entryId: entryId ?? "" } },
              ],
            ),
          };
        }
        throw error;
      }

      const tAfterCreate = Date.now();

      // WP-319 S2: Record grounding outcome for observability (STD-155).
      // Fire-and-forget — never block capture on observability failure.
      void kernelMutation("chain.recordGroundingOutcome", {
        surface: "mcp",
        promptShown: groundingReport.governance.length > 0,
        highestRatio: Math.max(
          ...groundingReport.duplicates.map((d: any) => d.overlapRatio),
          ...groundingReport.related.map((r: any) => r.overlapRatio),
          0,
        ),
        hadGovernance: groundingReport.governance.length > 0,
        grounding_ms: groundingElapsedMs,
      }).catch(() => undefined);

      // 6. Discover candidates + build batch relations array (BET-105 E4)
      const linksCreated: LinkResult[] = [];
      const linksSuggested: LinkSuggestion[] = [];
      const userLinkResults: Array<{ label: string; ok: boolean }> = [];
      // WP-474 E5: auto-links the server judge advised but did NOT write (untypeable) — surfaced, never silently dropped.
      const advisedLinks: Array<{ targetName: string; targetCollection: string; relationType: string }> = [];
      // BET-167: Conflict candidates for pre-commit advisory (populated from search results)
      let conflictCandidates: Array<{ entryId: string; name: string; collection: string }> = [];

      type PendingRelation = {
        fromEntryId: string; toEntryId: string; type: string;
        // WP-474 E4-REWIRE: provenance marker + carried confidence threaded structurally
        // (not just as a string in meta.linkReason) so createEntryRelations persists them.
        proposedBy?: string; confidence?: number;
        meta: { source: "auto" | "user"; targetName: string; targetCollection: string; linkReason?: string };
      };
      const pendingRelations: PendingRelation[] = [];

      const skipAutoDiscovery = links && links.length > 0;
      const searchQuery = extractSearchTerms(name, description);
      if (searchQuery && !skipAutoDiscovery) {
        // BET-281: Populate hub cache before scoring; uses collectionCache (no extra DB call).
        const hubSlugs = await populateHubCache();
        const [searchResults, allCollections] = await Promise.all([
          kernelQuery<any[]>("chain.searchEntries", { query: searchQuery }),
          getCollections(),
        ]);

        const collMap = new Map<string, string>();
        for (const c of allCollections) collMap.set(c._id, c.slug);

        const candidates = (searchResults ?? [])
          .filter((r) => r.entryId !== finalEntryId && r._id !== internalId)
          .map((r) => {
            const conf = computeLinkConfidence(r, name, description, resolvedCollection, collMap.get(r.collectionId) ?? "unknown", hubSlugs);
            return {
              ...r,
              collSlug: collMap.get(r.collectionId) ?? "unknown",
              confidence: conf.score,
              confidenceReason: conf.reason,
            };
          })
          .sort((a, b) => b.confidence - a.confidence);

        let autoCount = 0;
        for (const c of candidates) {
          if (autoCount >= MAX_AUTO_LINKS) break;
          if (c.confidence < AUTO_LINK_CONFIDENCE_THRESHOLD) break;
          if (!c.entryId || !finalEntryId) continue;

          const { type: relationType, reason: relationReason } = inferRelationType(resolvedCollection, c.collSlug, profile);
          pendingRelations.push({
            fromEntryId: finalEntryId, toEntryId: c.entryId, type: relationType,
            // WP-474 E4-REWIRE: auto-defaulted relation (no one chose the type) — mark it
            // as auto-link and carry the numeric confidence structurally.
            proposedBy: "auto-link", confidence: c.confidence,
            meta: { source: "auto", targetName: c.name, targetCollection: c.collSlug,
              linkReason: `confidence ${c.confidence} (${c.confidenceReason}) + ${relationReason}` },
          });
          autoCount++;
        }

        const autoTargetIds = new Set(pendingRelations.map(r => r.toEntryId));
        for (const c of candidates) {
          if (linksSuggested.length >= MAX_SUGGESTIONS) break;
          if (autoTargetIds.has(c.entryId)) continue;
          if (c.confidence < 10) continue;

          const preview = extractPreview(c.data, 80);
          const reason = c.confidence >= AUTO_LINK_CONFIDENCE_THRESHOLD
            ? "high relevance (already linked)"
            : `"${c.name.toLowerCase().split(/\s+/).filter((w: string) => `${name} ${description}`.toLowerCase().includes(w) && w.length > 3).slice(0, 2).join('", "')}" appears in content`;

          linksSuggested.push({ entryId: c.entryId, name: c.name, collection: c.collSlug, reason, preview });
        }

        // BET-167: Collect conflict candidates from auto-link search results for pre-commit advisory.
        // Reuses the search already done — no extra query needed.
        conflictCandidates = candidates
          .filter((c) => c.entryId && c.entryId !== finalEntryId)
          .slice(0, 3)
          .map((c) => ({ entryId: c.entryId as string, name: c.name as string, collection: c.collSlug as string }));
      }

      // 6b. Add user-specified links to batch
      if (links && links.length > 0 && finalEntryId) {
        for (const link of links) {
          pendingRelations.push({
            fromEntryId: finalEntryId, toEntryId: link.to, type: link.type,
            // WP-474 E4-REWIRE: user-chosen type — mark provenance, NO confidence (human-chosen).
            proposedBy: "user",
            meta: { source: "user", targetName: link.to, targetCollection: "unknown" },
          });
        }
      }

      // 7. Parallel post-create: batch relations + cardinality + contradiction + coaching
      const resolvedCK = canonicalKey;

      const batchRelationsPromise = pendingRelations.length > 0
        ? kernelMutation<{ results: Array<{ fromEntryId: string; toEntryId: string; type: string; ok: boolean; error?: string; status?: 'advised' }>; created: number; failed: number; advised: number }>(
            "chain.createEntryRelations", {
              // WP-474 E4-REWIRE: carry provenance + confidence through to the server seam.
              relations: pendingRelations.slice(0, 25).map(r => ({
                fromEntryId: r.fromEntryId, toEntryId: r.toEntryId, type: r.type,
                ...(r.proposedBy !== undefined ? { proposedBy: r.proposedBy } : {}),
                ...(r.confidence !== undefined ? { confidence: r.confidence } : {}),
              })),
              sessionId: agentId ?? undefined,
            },
          ).catch((err) => {
            entryWarnings.push(`Auto-linking partially failed: ${err instanceof Error ? err.message : "unknown error"}`);
            return null;
          })
        : Promise.resolve(null);

      const cardinalityPromise = resolvedCK
        ? kernelQuery<any>("chain.checkCardinalityWarning", { canonicalKey: resolvedCK }).catch(() => null)
        : Promise.resolve(null);

      const contradictionPromise = runContradictionCheck(name, description);

      // WP-475 E1: chain.createEntry now delivers the heuristic coaching verdict in its
      // response — consume it instead of re-running the evaluation (the second call
      // inserted a duplicate qualityVerdicts row and scheduled the background LLM judge
      // twice). The explicit kernelMutation survives ONLY as a fallback for older
      // deploys whose createEntry response lacks the coaching field (deploy skew).
      const coachingPromise = coachingFromCreate !== undefined
        ? Promise.resolve(coachingFromCreate)
        : kernelMutation<CoachingResultSubset | null>("quality.evaluateHeuristicAndSchedule", {
            entryId: finalEntryId, context: "capture",
          }).catch(() => null);

      const [batchResult, cardinalityCheck, contradictionWarnings, coachingAtCreate] = await Promise.all([
        batchRelationsPromise, cardinalityPromise, contradictionPromise, coachingPromise,
      ]);

      // WP-475 E1 (Codex P2 — CLI parity): the relations batch landed AFTER the
      // create-time verdict, so relation-dependent rubric criteria would render
      // falsely failed. When ANY relation was created (user links and/or
      // auto-discovered), re-evaluate and render THAT verdict — the re-eval also
      // schedules the LLM run that user-link captures deferred at create.
      // Fail-open: a re-eval failure keeps the create-time verdict.
      let verdictResult = coachingAtCreate;
      if ((batchResult?.created ?? 0) > 0) {
        const reEval = await kernelMutation<CoachingResultSubset | null>("quality.evaluateHeuristicAndSchedule", {
          entryId: finalEntryId, context: "capture",
        }).catch(() => null);
        if (reEval?.verdict) verdictResult = reEval;
      }

      const tAfterRelations = Date.now();

      // Map batch results back to linksCreated / userLinkResults
      if (batchResult) {
        for (let i = 0; i < pendingRelations.length; i++) {
          const pending = pendingRelations[i];
          const result = batchResult.results[i];
          if (!result) continue;

          if (pending.meta.source === "auto") {
            if (result.ok) {
              linksCreated.push({
                targetEntryId: pending.toEntryId, targetName: pending.meta.targetName,
                targetCollection: pending.meta.targetCollection, relationType: pending.type,
                linkReason: pending.meta.linkReason,
              });
            } else if (result.status === 'advised') {
              advisedLinks.push({
                targetName: pending.meta.targetName, targetCollection: pending.meta.targetCollection,
                relationType: pending.type,
              });
            }
          } else {
            if (result.ok) {
              userLinkResults.push({ label: `✓ ${pending.type} → ${pending.toEntryId}`, ok: true });
              linksCreated.push({
                targetEntryId: pending.toEntryId, targetName: pending.meta.targetName,
                targetCollection: pending.meta.targetCollection, relationType: pending.type,
              });
            } else {
              userLinkResults.push({ label: `✗ ${pending.type} → ${pending.toEntryId}: ${result.error ?? "failed"}`, ok: false });
            }
          }
        }
      }

      // WP-474 E5: surface auto-links the judge advised but did not write — never silently dropped.
      if (advisedLinks.length > 0) {
        entryWarnings.push(`${advisedLinks.length} auto-link(s) advised, not written (untypeable) — accept with \`relations action=create\` to promote.`);
      }

      // 8. WP-480 S1: the in-process client scorecard was deleted — quality is
      // judged ONCE server-side. The verdict rides `verdictResult` (rubric coaching
      // below) and the advisory `formativeHints`; the connector only renders them.
      const tAfterQuality = Date.now();

      const cardinalityWarning: string | null = cardinalityCheck?.warning ?? null;

      if (contradictionWarnings.length > 0) {
        await recordSessionActivity({ contradictionWarning: true });
      }

      let coachingSection = "";
      if (verdictResult?.verdict && verdictResult.verdict.tier !== "passive" && verdictResult.verdict.criteria.length > 0) {
        coachingSection = formatRubricCoaching(verdictResult);
      }

      // 11. Track quality verdict
      if (verdictResult?.verdict) {
        try {
          const wsForTracking = await getWorkspaceContext();
          const v = verdictResult.verdict;
          const failedCount = (v.criteria ?? []).filter((c: any) => !c.passed).length;
          trackQualityVerdict(wsForTracking.workspaceId, {
            entry_id: finalEntryId,
            entry_type: v.canonicalKey ?? resolvedCollection,
            tier: v.tier,
            context: "capture",
            passed: v.passed,
            source: verdictResult.source ?? "heuristic",
            criteria_total: v.criteria?.length ?? 0,
            criteria_failed: failedCount,
            llm_scheduled: v.tier !== "passive",
            methodology_version: v.methodologyVersion,
          });
        } catch { /* tracking is advisory */ }
      }

      // 12. Auto-commit if requested (or defaulted via Open governance mode).
      // BET-76 FEAT-111: When autoCommit is not explicitly set, default to true for
      // Open mode workspaces. Consensus/role modes retain draft-first behavior.
      // TEN-1810: Refresh governanceMode in case workspace flipped to Open after session start.
      const freshGovernanceMode = await refreshWorkspaceGovernanceMode();
      const shouldAutoCommit = shouldAutoCommitCapture(autoCommit, freshGovernanceMode);

      // BET-167 doneWhen #4: Conflict pre-check before auto-commit (advisory, never blocking).
      // When auto-link discovery was skipped (user provided explicit links), run a lightweight
      // search now so conflict candidates are populated before the commit.
      if (shouldAutoCommit && finalEntryId && conflictCandidates.length === 0) {
        try {
          const conflictResults = await kernelQuery<any[]>("chain.searchEntries", { query: name });
          conflictCandidates = (conflictResults ?? [])
            .filter((r: any) => r.entryId && r.entryId !== finalEntryId && r._id !== internalId)
            .slice(0, 3)
            .map((r: any) => ({ entryId: r.entryId as string, name: r.name as string, collection: r.collectionSlug ?? "unknown" }));
        } catch {
          // Advisory only — proceed without conflict check on failure
        }
      }

      let finalStatus: "draft" | "committed" | "proposed" | "draft_on_failure" = "draft";
      let commitError: string | null = null;
      // WP-465 surface parity: full refusal payload (routes + mode) for the coherency refused path.
      let commitRefusal: Record<string, unknown> | null = null;
      // DEC-896 / BR-76 S3: server already committed in Open mode — skip commitEntry entirely.
      if (wasAutoCommittedServerSide) {
        finalStatus = "committed";
        await recordSessionActivity({ entryModified: internalId });
        trackChainEntryCommitted(wsCtx.workspaceId, {
          entry_id: finalEntryId,
          collection: resolvedCollection,
          commit_method: "auto",
          surface: "mcp_capture",
        });
      } else if (shouldAutoCommit && finalEntryId) {
        try {
          const semanticConflicts = await runSemanticConflictPreflight(name, description, resolvedCollection);
          const blockingConflicts = semanticConflicts.filter(
            (conflict) => conflict.type === "contradiction" && conflict.confidence >= BLOCKING_CONTRADICTION_THRESHOLD,
          );

          if (blockingConflicts.length > 0) {
            commitError = `blocked by semantic contradiction preflight: ${blockingConflicts[0]?.chainEntryId} ${blockingConflicts[0]?.explanation ?? ""}`.trim();
          } else {
            const commitResult = await kernelMutation<any>("chain.commitEntry", {
              entryId: finalEntryId,
              author: agentId ? `agent:${agentId}` : undefined,
              sessionId: agentId ?? undefined,
            });
            const refusalReason = coherencyRefusalReason(commitResult);
            if (refusalReason) {
              // WP-465 slice ⑤ (Option B): the entry stays a draft — never accepted/tracked.
              commitError = `coherency gate refused this accept — ${refusalReason}`;
              // WP-465 surface parity: thread full refusal payload so routes + mode reach the caller.
              commitRefusal = (commitResult?.refusal as Record<string, unknown> | undefined) ?? null;
              finalStatus = "draft_on_failure";
            } else {
              finalStatus = commitResult?.status === "proposal_created" ? "proposed" : "committed";
              if (finalStatus === "committed") {
                await recordSessionActivity({ entryModified: internalId });
                trackChainEntryCommitted(wsCtx.workspaceId, {
                  entry_id: finalEntryId,
                  collection: resolvedCollection,
                  commit_method: "auto",
                  surface: "mcp_capture",
                });
              }
            }
          }
        } catch (e: unknown) {
          // BR-76 S2 — DEC-888: Surface failures via log + payload + session TEN. Never silent.
          commitError = e instanceof Error ? e.message : "unknown error";
          finalStatus = "draft_on_failure";
          await recordCommitFailure({ entryId: finalEntryId, error: e, sessionId: agentId, server });
        }
      }

      // 13. Format response
      const lines: string[] = [
        `# Captured: ${finalEntryId || name}`,
        `**${name}** added to \`${resolvedCollection}\` as \`${finalStatus}\``,
        `**Workspace:** ${wsCtx.workspaceSlug} (${wsCtx.workspaceId})`,
      ];

      if (classifierMeta) {
        lines.push("");
        lines.push("## Classification");
        if (classifierMeta.abstained) {
          lines.push(`No classifier coverage for \`${resolvedCollection}\` — entry accepted as-is.`);
        } else if (classifierMeta.autoRouted) {
          lines.push(`Auto-routed to \`${resolvedCollection}\` (${classifierMeta.topConfidence}% confidence).`);
          if (classifierMeta.reasons.length > 0) {
            lines.push(`Reason: ${classifierMeta.reasons.join("; ")}.`);
          }
          lines.push(`Override: rerun with explicit \`collection\` if wrong.`);
        } else if (classifierMeta.agrees) {
          lines.push(`Classifier confirms \`${resolvedCollection}\` (${classifierMeta.topConfidence}% confidence).`);
        } else {
          const suggested = classifierMeta.candidates[0]?.collection ?? "unknown";
          lines.push(`Classifier suggests \`${suggested}\` (${classifierMeta.topConfidence}% confidence) — review classification.`);
          if (classifierMeta.reasons.length > 0) {
            lines.push(`Reason: ${classifierMeta.reasons.join("; ")}.`);
          }
          if (classifierMeta.overrideCommand) {
            lines.push(`Override: \`${classifierMeta.overrideCommand}\``);
          }
        }
      }

      // DEC-70: Cortex link for chain entries (e.g. bets) — structuredContent studioUrl
      const appUrl = process.env.PRODUCTBRAIN_APP_URL ?? "https://work.productbrain.io";
      const studioUrl =
        resolvedCollection === "chains"
          ? `${appUrl.replace(/\/$/, "")}/w/${wsCtx.workspaceSlug}/legacy/${internalId}`
          : undefined;
      if (studioUrl) {
        lines.push("");
        lines.push(`**View in Cortex:** ${studioUrl}`);
      }

      if (linksCreated.length > 0) {
        lines.push("");
        lines.push(`## Auto-linked (${linksCreated.length})`);
        for (const link of linksCreated) {
          const reason = (link as any).linkReason ? ` — because ${(link as any).linkReason}` : "";
          lines.push(`- -> **${link.relationType}** ${link.targetEntryId}: ${link.targetName} [${link.targetCollection}]${reason}`);
        }
      }

      if (userLinkResults.length > 0) {
        lines.push("");
        lines.push("## Requested links");
        for (const r of userLinkResults) lines.push(`- ${r.label}`);
      }

      if (finalStatus === "committed") {
        const wasAutoCommitted = autoCommit === undefined && freshGovernanceMode === "open";
        lines.push("");
        lines.push(`## Accepted: ${finalEntryId}`);
        if (wasAutoCommitted) {
          lines.push(`**${name}** added to your knowledge base.`);
        } else {
          lines.push(`**${name}** promoted to SSOT on the Chain.`);
        }
        if (await isGoverned(resolvedCollection)) {
          lines.push(`_Note: \`${resolvedCollection}\` is a governed collection — ensure this entry has been reviewed._`);
        }
      } else if (finalStatus === "proposed") {
        lines.push("");
        lines.push(`## Proposal created: ${finalEntryId}`);
        lines.push(`**${name}** requires consent before it can be accepted, so a proposal was created instead of accepting directly.`);
      } else if (commitError) {
        lines.push("");
        lines.push("## Accept failed");
        if (commitRefusal) {
          // WP-465 surface parity: render routes + mode from the structured coherency refusal.
          // MINOR 4: demote to H3 so the embedded refusal sits correctly under the H2 "## Accept failed".
          const refusalText = coherencyRefusedResult("capture", finalEntryId, commitRefusal, { headingLevel: 3 }).content[0].text;
          lines.push(...refusalText.split("\n"));
          lines.push(`Entry saved as draft — use \`entries action=commit entryId="${finalEntryId}"\` to promote when ready.`);
        } else {
          lines.push(`Error: ${commitError}. Entry saved as draft — use \`entries action=commit entryId="${finalEntryId}"\` to promote when ready.`);
        }
      }

      // BET-167: Advisory conflict notice — shown after commit status, never blocking
      if (shouldAutoCommit && conflictCandidates.length > 0) {
        lines.push("");
        lines.push("## Potential Overlaps");
        lines.push(`Auto-accepted with advisory: ${conflictCandidates.length} similar ${conflictCandidates.length === 1 ? "entry" : "entries"} found.`);
        for (const c of conflictCandidates) {
          lines.push(`- \`${c.entryId}\`: ${c.name} [${c.collection}]`);
        }
        lines.push("Consider reviewing for duplicates.");
      }

      if (linksSuggested.length > 0) {
        lines.push("");
        lines.push("## Suggested links (review and use `relations action=create`)");
        for (let i = 0; i < linksSuggested.length; i++) {
          const s = linksSuggested[i];
          const preview = s.preview ? ` — ${s.preview}` : "";
          lines.push(`${i + 1}. **${s.entryId ?? "(no ID)"}**: ${s.name} [${s.collection}]${preview}`);
        }
      }

      // WP-318 S2 E4: Grounding report — format:human renders compact summary; format:agent exposes via structuredContent only
      if (format === "human") {
        const hasDuplicates = groundingReport.duplicates.length > 0;
        const hasRelated = groundingReport.related.length > 0;
        const hasGov = groundingReport.governance.length > 0;
        if (hasDuplicates || hasRelated || hasGov) {
          lines.push("");
          const parts: string[] = [];
          if (hasDuplicates) parts.push(`${groundingReport.duplicates.length} possible duplicate${groundingReport.duplicates.length > 1 ? "s" : ""}`);
          if (hasRelated) parts.push(`${groundingReport.related.length} related entr${groundingReport.related.length > 1 ? "ies" : "y"}`);
          if (hasGov) parts.push(`${groundingReport.governance[0]?.entryId} may govern this`);
          lines.push(`_Grounding: ${parts.join(", ")}. Review and link if relevant._`);
        }
      }

      // BET-289 S2: Field-level writing guidance — shown when collection fields have guidance
      const fieldGuidanceSection = formatFieldGuidance(col.fields ?? []);
      if (fieldGuidanceSection) {
        lines.push("");
        lines.push(fieldGuidanceSection);

        // BET-289 S5: Track guidance application for observability (STD-155)
        const guidedFieldCount = (col.fields ?? []).filter((f: { writingGuidance?: string }) => f.writingGuidance).length;
        trackFieldGuidanceApplied(wsCtx.workspaceId, {
          collection: resolvedCollection,
          guided_field_count: guidedFieldCount,
        });
      }

      // BET-272 S3: Formative quality hints — advisory, never blocking (BR-144)
      if (formativeHints.length > 0) {
        lines.push("");
        lines.push("## Quality Hints (advisory)");
        for (const hint of formativeHints) {
          lines.push(`- **${hint.field}:** ${hint.hint}`);
        }
        lines.push(`_These hints are advisory — your entry was captured. Use \`entries action=update entryId="${finalEntryId}"\` to address them._`);
      }

      // BET-272 S5: Advisory relation suggestions from graph (BR-144, STD-147)
      if (relationSuggestionsFromCreate.length > 0) {
        lines.push("");
        lines.push("## Suggested Relations (advisory)");
        for (const rs of relationSuggestionsFromCreate) {
          lines.push(`- **${rs.relationType}** → ${rs.targetEntryId} "${rs.targetName}" (confidence: ${rs.confidence})`);
        }
        lines.push(`_Create with: \`relations action=create fromId="${finalEntryId}" toId="TARGET-ID" type="TYPE"\`_`);
      }

      // TEN-2365: capture-time authority-domain filing — PENDING proposal, never auto-ratified (PAT-251)
      if (authorityDomainFromCreate) {
        lines.push("");
        lines.push(`domain: ${authorityDomainFromCreate.slug} (proposal pending — ratify at session close)`);
      }

      // STD-155: Track capture intelligence observability
      if (formativeHints.length > 0) {
        trackCaptureQualityHints(wsCtx.workspaceId, {
          collection: resolvedCollection,
          hint_count: formativeHints.length,
          hint_fields: formativeHints.map((h) => h.field),
        });
      }
      if (relationSuggestionsFromCreate.length > 0) {
        trackCaptureRelationSuggestions(wsCtx.workspaceId, {
          collection: resolvedCollection,
          suggestion_count: relationSuggestionsFromCreate.length,
          relation_types: relationSuggestionsFromCreate.map((rs) => rs.relationType),
          avg_confidence: Math.round(
            relationSuggestionsFromCreate.reduce((sum, rs) => sum + rs.confidence, 0) /
              relationSuggestionsFromCreate.length,
          ),
        });
      }

      // Strategy-link warning for bet/goal (ENT-ldomlr: Intelligence Surface)
      const isBetOrGoal =
        isBetCapture ||
        resolvedCK === "bet" ||
        resolvedCK === "goal";
      const hasStrategyLink = linksCreated.some((l) => l.targetCollection === "strategy");
      if (isBetOrGoal && !hasStrategyLink) {
        lines.push("");
        lines.push(
          `**Strategy link:** This ${isBetCapture ? "bet" : "goal"} doesn't connect to any strategy entry. Consider linking before accepting. Use \`relations action=suggest entryId="${finalEntryId}"\` to find strategy entries to connect to.`
        );
        await recordSessionActivity({ strategyLinkWarnedForEntryId: internalId });
      }

      // Cardinality warning for singleton types (DEC-zcuhvb)
      if (cardinalityWarning) {
        lines.push("");
        lines.push(`**Cardinality warning:** ${cardinalityWarning}`);
      }

      // Contradiction warnings — exact format per spec
      if (contradictionWarnings.length > 0) {
        lines.push("");
        lines.push("⚠ Contradiction check: proposed entry matched existing governance entries:");
        for (const w of contradictionWarnings) {
          lines.push(`- ${w.name} (${w.collection}, ${w.entryId}) — has 'governs' relation to ${w.governsCount} entries`);
        }
        lines.push("Run `context action=gather` on these entries before accepting.");
      }

      // Validation warnings from createEntry (FEAT-188: MCP Envelope Warnings)
      if (entryWarnings.length > 0) {
        lines.push("");
        lines.push("## Validation Warnings");
        for (const w of entryWarnings) {
          lines.push(`- ${w}`);
        }
      }

      // Rubric coaching (assertive/nudge only)
      if (coachingSection) {
        lines.push("");
        lines.push(coachingSection);
      }

      // Next steps: adapt based on what was already done
      // WP-480 S1: "has quality gaps" is now sourced from the SERVER verdict +
      // advisory formative hints (the deleted client scorecard used to drive this).
      const hasQualityGaps =
        (verdictResult?.verdict != null && verdictResult.verdict.passed === false) ||
        formativeHints.length > 0;
      lines.push("");
      lines.push("## Next Steps");
      const eid = finalEntryId || "(check entry ID)";
      if (finalStatus === "committed") {
        lines.push(`1. **Connect it:** \`relations action=suggest entryId="${eid}"\` — discover additional links`);
        if (hasQualityGaps) {
          lines.push(`2. **Improve quality:** \`entries action=update entryId="${eid}"\` — fill missing fields`);
        }
      } else if (finalStatus === "proposed") {
        lines.push(`1. **Connect it:** \`relations action=suggest entryId="${eid}"\` — discover additional links to support the proposal`);
        if (hasQualityGaps) {
          lines.push(`2. **Improve quality:** \`entries action=update entryId="${eid}"\` — strengthen the entry before approval`);
        }
      } else {
        if (userLinkResults.length === 0) {
          lines.push(`1. **Connect it:** \`relations action=suggest entryId="${eid}"\` — discover what this should link to`);
        }
        lines.push(`${userLinkResults.length === 0 ? "2" : "1"}. **Accept it:** \`entries action=commit entryId="${eid}"\` — promote from draft to SSOT on the Chain`);
        if (hasQualityGaps) {
          lines.push(`${userLinkResults.length === 0 ? "3" : "2"}. **Improve quality:** \`entries action=update entryId="${eid}"\` — fill missing fields`);
        }
      }

      // Advisory: workspace readiness hints (never blocking)
      try {
        const readiness = await kernelQuery<any>("chain.workspaceReadiness");
        if (readiness && readiness.gaps && readiness.gaps.length > 0) {
          const topGaps = readiness.gaps.slice(0, 2);
          lines.push("");
          lines.push(`## Workspace Readiness: ${readiness.score}%`);
          for (const gap of topGaps) {
            lines.push(`- _${gap.label}:_ ${gap.guidance}`);
          }
        }
      } catch {
        // Readiness check is advisory — capture works without it
      }

      // Context-sensitive next: adapt based on what was already done
      const next: NextAction[] = [];
      if (finalStatus === "committed") {
        next.push({ tool: "relations", description: "Discover connections", parameters: { action: "suggest", entryId: finalEntryId } });
      } else if (finalStatus === "proposed") {
        next.push({ tool: "relations", description: "Discover connections", parameters: { action: "suggest", entryId: finalEntryId } });
      } else {
        if (userLinkResults.length === 0) {
          next.push({ tool: "relations", description: "Discover links", parameters: { action: "suggest", entryId: finalEntryId } });
        }
        next.push({ tool: "entries", description: "Accept onto Chain", parameters: { action: "commit", entryId: finalEntryId } });
      }

      // WP-480 S1: quality wording comes from the server rubric verdict (single
      // scorer, DEC-1418), not a client N/10 recompute.
      const qualityPhrase = verdictQualityPhrase(verdictResult);
      const summary = finalStatus === "committed"
        ? `Captured and accepted ${finalEntryId} (${name}) onto ${resolvedCollection}. ${qualityPhrase}`
        : finalStatus === "proposed"
          ? `Captured ${finalEntryId} (${name}) in ${resolvedCollection} and created a proposal for acceptance. ${qualityPhrase}`
          : `Captured ${finalEntryId} (${name}) as draft in ${resolvedCollection}. ${qualityPhrase}`;

      const expectedFields = (col.fields ?? []).map((f: any) => ({
        key: f.key, type: f.type, ...(f.required && { required: true }),
      }));

      // WP-316 S1a: Build CaptureContract for the EnvelopeSuccess.contract field.
      // Agents can read required fields upfront without a separate round-trip.
      const captureContractForEnvelope = {
        requiredFields: (col.fields ?? []).filter((f) => f.required === true).map((f) => f.key),
        allFields: (col.fields ?? []).map((f) => ({
          key: f.key,
          required: f.required === true,
          type: f.type,
          ...(f.helpText ? { helpText: f.helpText } : {}),
        })),
        ...(col.captureExpectation ? { captureExpectation: col.captureExpectation } : {}),
      };

      const totalMs = Date.now() - timingStart;
      const timing = {
        classifyMs: tAfterClassify - timingStart,
        createMs: tAfterCreate - tAfterClassify,
        autoLinkMs: tAfterRelations - tAfterCreate,
        qualityMs: tAfterQuality - tAfterRelations,
        totalMs,
      };

      const toolResult = {
        content: [{ type: "text" as const, text: lines.join("\n") }],
        structuredContent: {
          ...success(
            summary,
            {
              entryId: finalEntryId,
              collection: resolvedCollection,
              name,
              status: finalStatus,
              // WP-465 surface parity: thread full refusal (routes + mode) for coherency-refused auto-commits.
              ...(commitRefusal ? { coherencyRefusal: commitRefusal } : {}),
              // WP-480 S1: the server verdict rides `qualityVerdict` (named breaking
              // output-contract change, pre-launch).
              qualityVerdict: verdictResult?.verdict
                ? { ...verdictResult.verdict, source: verdictResult.source ?? "heuristic" }
                : undefined,
              ...(classifierMeta && { classifier: classifierMeta }),
              ...(studioUrl && { studioUrl }),
              ...(entryWarnings.length > 0 && { warnings: entryWarnings }),
              ...(normalizationMeta && (Object.keys(normalizationMeta.remapped).length > 0 || normalizationMeta.rejected.length > 0) && {
                normalization: {
                  remapped: normalizationMeta.remapped,
                  rejected: normalizationMeta.rejected,
                },
              }),
              expectedFields,
              // BET-272 S3: Advisory quality hints — always included (empty array when all criteria pass)
              qualityHints: formativeHints,
              // BET-272 S5: Advisory relation suggestions from graph (BR-144, STD-147)
              relationSuggestions: relationSuggestionsFromCreate,
              // TEN-2365: capture-time authority-domain proposal (PENDING ratification), when filed.
              ...(authorityDomainFromCreate && { authorityDomain: authorityDomainFromCreate }),
              // WP-318 S2: Pre-write grounding report — always included (empty arrays when no suggestions)
              groundingReport,
            },
            next,
          ),
          // WP-316 S1a: Populate contract field so agents know required fields upfront.
          contract: captureContractForEnvelope,
          _meta: { timing },
        },
      };

      return toolResult;
}

/**
 * WP-484 S2 (§3): extracted batch-capture handler — reused by the legacy
 * `batch-capture` standalone tool AND the new `capture action=batch`
 * (absorbed per §3 core-nouns table). One business-logic path. TEN-957:
 * each entry may carry inline `relations` created right after capture.
 */
export async function handleBatchCapture(
  server: McpServer,
  { entries, autoCommit, preview }: z.infer<typeof batchCaptureSchema>,
) {
      requireWriteAccess();

      const batchTimingStart = Date.now();

      const agentId = getAgentSessionId();
      const createdBy = agentId ? `agent:${agentId}` : "capture";
      const wsCtx = await getWorkspaceContext();
      // TEN-1810: Refresh governanceMode in case workspace flipped to Open after session start.
      const freshGovernanceModeForBatch = await refreshWorkspaceGovernanceMode();
      const autoCommitApplied = shouldAutoCommitCapture(autoCommit, freshGovernanceModeForBatch);

      // ── FEAT-160: Parallel classification for entries without explicit collection ──
      const needsClassification = entries
        .map((e, i) => ({ ...e, index: i }))
        .filter((e) => !e.collection);

      let classificationMap = new Map<number, ResolvedCollection | null>();
      if (needsClassification.length > 0) {
        await server.sendLoggingMessage({
          level: "info",
          data: `Classifying ${needsClassification.length}/${entries.length} entries via LLM...`,
          logger: "product-brain",
        });
        const classifications = await Promise.all(
          needsClassification.map((e) =>
            resolveCollection({ name: e.name, description: e.description }).catch(() => null),
          ),
        );
        for (let i = 0; i < needsClassification.length; i++) {
          classificationMap.set(needsClassification[i].index, classifications[i]);
        }
      }

      type BatchEntryResult = {
        name: string;
        collection: string;
        entryId: string;
        ok: boolean;
        autoLinks: number;
        // WP-474 E5: auto-links the judge advised but did NOT write (untypeable).
        advisedLinks: number;
        status: "draft" | "committed" | "proposed" | "draft_on_failure";
        classifiedBy?: "llm" | "heuristic" | "explicit";
        confidence?: number;
        confidenceTier?: "high" | "medium" | "low";
        error?: string;
        commitError?: string;
        // WP-465 surface parity: full coherency refusal payload for batch-path refused auto-commits.
        commitRefusal?: Record<string, unknown>;
        warnings?: string[];
        normalization?: { remapped: Record<string, string>; rejected: string[] };
        // BET-167: Number of similar entries found during conflict pre-check
        overlapCount?: number;
        // TEN-2365: capture-time authority-domain proposal (PENDING ratification), when filed.
        authorityDomain?: { slug: string; status: "proposal-pending" };
        // TEN-957 (WP-484 S2): inline relations created right after this entry.
        relationsCreated?: number;
        // Finding #14: inline relations that filed an agent proposal instead of writing.
        relationsProposed?: number;
      };
      const results: BatchEntryResult[] = [];
      type SkippedEntry = {
        index: number;
        name: string;
        suggestedCollection?: string;
        confidence?: number;
        alternatives?: Array<{ collection: string; confidence: number }>;
      };
      const skippedLowConfidence: SkippedEntry[] = [];

      await server.sendLoggingMessage({
        level: "info",
        data: `Batch capturing ${entries.length} entries...`,
        logger: "product-brain",
      });

      // BET-281: Use collectionCache instead of direct listCollections call.
      const allCollections = await getCollections();
      const hubSlugs = await populateHubCache();
      const collCache = new Map<string, CollectionDoc>();
      for (const c of allCollections) collCache.set(c.slug, c);
      const collIdToSlug = new Map<string, string>();
      for (const c of allCollections) collIdToSlug.set(c._id, c.slug);

      for (let entryIdx = 0; entryIdx < entries.length; entryIdx++) {
        const entry = entries[entryIdx];

        if (entryIdx > 0 && entryIdx % 5 === 0) {
          await server.sendLoggingMessage({
            level: "info",
            data: `Captured ${entryIdx}/${entries.length} entries...`,
            logger: "product-brain",
          });
        }

        // ── Resolve collection: explicit or classified ──
        let resolvedSlug: string | undefined = entry.collection;
        let classifiedBy: "llm" | "heuristic" | "explicit" | undefined;
        let confidence: number | undefined;
        let confidenceTier: "high" | "medium" | "low" | undefined;

        if (resolvedSlug) {
          classifiedBy = "explicit";
        } else {
          const resolved = classificationMap.get(entryIdx);
          // BET-288 S0: Fire confusion telemetry for batch classifications
          if (resolved) {
            emitClassificationTelemetry(resolved, wsCtx.workspaceId, {
              explicitCollectionProvided: false,
              autoRouted: resolved.tier !== "low",
            });
          }
          if (!resolved || resolved.tier === "low") {
            skippedLowConfidence.push({
              index: entryIdx,
              name: entry.name,
              suggestedCollection: resolved?.collection,
              confidence: resolved?.confidence,
              alternatives: resolved?.alternatives.slice(0, 3),
            });
            continue;
          }
          resolvedSlug = resolved.collection;
          classifiedBy = resolved.classifiedBy;
          confidence = resolved.confidence;
          confidenceTier = resolved.tier;
        }

        const profile = await getProfile(resolvedSlug);
        const col = collCache.get(resolvedSlug);

        if (!col) {
          results.push({
            name: entry.name,
            collection: resolvedSlug,
            entryId: "",
            ok: false,
            autoLinks: 0,
            advisedLinks: 0,
            status: "draft",
            classifiedBy,
            confidence,
            confidenceTier,
            error: `Collection "${resolvedSlug}" not found`,
          });
          continue;
        }

        // BR-111: entryId only allowed for business-rules and standards
        if (entry.entryId && resolvedSlug !== "business-rules" && resolvedSlug !== "standards") {
          results.push({
            name: entry.name,
            collection: resolvedSlug,
            entryId: "",
            ok: false,
            autoLinks: 0,
            advisedLinks: 0,
            status: "draft",
            classifiedBy,
            confidence,
            confidenceTier,
            // BR-111
            error: `entryId only allowed for business-rules and standards. Omit for ${resolvedSlug}.`,
          });
          continue;
        }

        const data = buildDataFromFields(col.fields ?? [], profile.descriptionField, entry.description);

        for (const def of profile.defaults) {
          if (def.value !== "infer" && def.value !== "today") data[def.key] = def.value;
        }

        if (profile.inferField) {
          const inferred = profile.inferField({
            collection: resolvedSlug, name: entry.name, description: entry.description,
            data, entryId: "", linksCreated: [], linksSuggested: [], collectionFields: col.fields ?? [],
          });
          for (const [key, val] of Object.entries(inferred)) {
            if (val !== undefined && val !== "") data[key] = val;
          }
        }

        if (isEmptyValue(data.date)) {
          const inferredSourceDate = inferSourceDate(entry.name, entry.description);
          if (inferredSourceDate && (col.fields ?? []).some((field) => field.key === "date")) {
            data.date = inferredSourceDate;
          }
        }

        if (!data[profile.descriptionField] && !data.description && !data.canonical) {
          data[profile.descriptionField || "description"] = entry.description;
        }

        if (entry.data && typeof entry.data === "object") {
          for (const [k, v] of Object.entries(entry.data)) {
            if (v !== undefined && v !== null && v !== "") data[k] = v;
          }
        }

        // DEC-231: work-packages collection is canonical — collection slug is the sole discriminator
        // DEC-313 GAP 2: also detect work package via canonicalKey from batch input
        const batchIsBet = resolvedSlug === "work-packages" || entry.canonicalKey === "work_package";
        // BET-288: derive strategy check from thinkingLayer instead of hardcoded slug
        const batchIsStrategy = col?.thinkingLayer === "strategy";
        const shouldDecomposeBatch =
          (batchIsStrategy || batchIsBet) && entry.description?.trim();
        let batchDecomposeWarning: string | undefined;
        if (shouldDecomposeBatch) {
          try {
            const decomposed = await kernelCall<{ decomposed: Record<string, unknown> } | null>(
              "chain.decomposeContent",
              {
                collectionSlug: resolvedSlug,
                entryName: entry.name,
                description: entry.description,
                existingData: data,
                ...(batchIsBet ? { canonicalKey: "work_package" } : {}),
                ...(col?.thinkingLayer ? { thinkingLayer: col.thinkingLayer } : {}),
              },
            );
            if (decomposed?.decomposed) {
              for (const [key, val] of Object.entries(decomposed.decomposed)) {
                if (isEmptyValue(data[key]) && isNonEmptyValue(val)) {
                  data[key] = val;
                }
              }
            }
          } catch {
            batchDecomposeWarning = "Content decomposition skipped (transient error). Entry saved with description only.";
          }
        }
        canonicalizeSelects(data, col.fields ?? [], batchIsBet);

        // BET-132: bet captures keep flat fields — no data.links wrapping

        try {
          const result = await kernelMutation<{
            preview?: boolean;
            docId: string; entryId: string;
            /** DEC-896 / BR-76: 'active' means server auto-committed (Open mode). Guard skips commitEntry. */
            status?: "draft" | "active" | "deprecated";
            warnings?: string[];
            normalization?: { remapped: Record<string, string>; rejected: string[]; accepted: string[] };
            // TEN-2365: capture-time authority-domain proposal (PENDING ratification), when filed.
            authorityDomain?: { slug: string; status: "proposal-pending" };
          }>("chain.createEntry", {
            collectionSlug: resolvedSlug,
            entryId: entry.entryId ?? undefined,
            name: entry.name,
            // DEC-896 / BR-76: omit status — server resolves from governanceMode (Open→active, else draft).
            data,
            createdBy,
            sessionId: agentId ?? undefined,
            canonicalKey: entry.canonicalKey ?? undefined,
            // BR-76 S2: Distinguish batch provenance in retrospective metrics (path a).
            originDetailOverride: "mcp-batch-capture",
            ...(entry.sourceRef ? { sourceRef: entry.sourceRef } : {}),
            ...(entry.sourceExcerpt ? { sourceExcerpt: entry.sourceExcerpt } : {}),
            ...(preview ? { preview: true } : {}),
          });
          const internalId = result.docId;
          const finalEntryId = result.entryId;
          const batchEntryWarnings = [
            ...(batchDecomposeWarning ? [batchDecomposeWarning] : []),
            ...(result.warnings ?? []),
          ];
          resolveGapsForEntry(entry.name, result.entryId);
          // DEC-896 / BR-76 S3: server already committed (Open mode auto-commit). Skip commitEntry.
          const batchWasAutoCommittedServerSide = result.status === "active";
          let finalStatus: "draft" | "committed" | "proposed" | "draft_on_failure" = batchWasAutoCommittedServerSide ? "committed" : "draft";
          let commitError: string | undefined;
          // WP-465 surface parity: full refusal payload for coherency-refused batch auto-commits.
          let commitRefusal: Record<string, unknown> | undefined;
          if (batchWasAutoCommittedServerSide) {
            await recordSessionActivity({ entryModified: internalId });
            trackChainEntryCommitted(wsCtx.workspaceId, {
              entry_id: finalEntryId,
              collection: resolvedSlug ?? undefined,
              commit_method: "auto",
              surface: "mcp_capture",
            });
          }

          let autoLinkCount = 0;
          // WP-474 E5: auto-links the judge advised but did NOT write (untypeable) — per-entry tally.
          let advisedLinkCount = 0;
          // BET-167: Track overlaps from search results for conflict advisory
          let entryOverlapCount = 0;
          const searchQuery = extractSearchTerms(entry.name, entry.description);
          if (searchQuery) {
            try {
              const searchResults = await kernelQuery<any[]>("chain.searchEntries", { query: searchQuery });
              const candidates = (searchResults ?? [])
                .filter((r) => r.entryId !== finalEntryId)
                .map((r) => {
                  const conf = computeLinkConfidence(r, entry.name, entry.description, resolvedSlug!, collIdToSlug.get(r.collectionId) ?? "unknown", hubSlugs);
                  return { ...r, collSlug: collIdToSlug.get(r.collectionId) ?? "unknown", confidence: conf.score };
                })
                .sort((a, b) => b.confidence - a.confidence);

              // BET-167: Count similar entries for conflict pre-check (reuses search, no extra query)
              entryOverlapCount = candidates.filter((c) => c.entryId).length;

              // WP-474 E4-REWIRE: batch auto-links carry provenance + confidence like the single path.
              const batchAutoLinks: Array<{ fromEntryId: string; toEntryId: string; type: string; proposedBy: string; confidence: number }> = [];
              for (const c of candidates) {
                if (batchAutoLinks.length >= MAX_AUTO_LINKS) break;
                if (c.confidence < AUTO_LINK_CONFIDENCE_THRESHOLD) break;
                if (!c.entryId) continue;
                const { type: relationType } = inferRelationType(resolvedSlug!, c.collSlug, profile);
                batchAutoLinks.push({ fromEntryId: finalEntryId, toEntryId: c.entryId, type: relationType, proposedBy: "auto-link", confidence: c.confidence });
              }

              if (batchAutoLinks.length > 0 && preview) {
                // Finding #13: chain.createEntryRelations (the batch/plural mutation) has
                // NO preview arg — unlike the singular chain.createEntryRelation used just
                // below. A previewed createEntry (preview:true, no DB write) was still
                // followed by a REAL relation write here whenever matching targets existed.
                // Skip the write entirely in preview mode; report what would happen instead.
                batchEntryWarnings.push(`${batchAutoLinks.length} auto-link(s) would be created (preview — no DB writes).`);
              } else if (batchAutoLinks.length > 0) {
                const batchRes = await kernelMutation<{ created: number; advised: number }>("chain.createEntryRelations", {
                  relations: batchAutoLinks, sessionId: agentId ?? undefined,
                });
                autoLinkCount = batchRes.created;
                // WP-474 E5: surface auto-links the judge advised but did not write — never silently dropped.
                advisedLinkCount = batchRes.advised ?? 0;
                if (advisedLinkCount > 0) {
                  batchEntryWarnings.push(`${advisedLinkCount} auto-link(s) advised, not written (untypeable) — accept with \`relations action=create\` to promote.`);
                }
              }
            } catch { /* search or batch link failed; entry still created */ }
          }

          // TEN-957 (WP-484 S2): inline per-item relations — validate-all-then-apply
          // (PAT-268 item 1) against the ALREADY-VALIDATED batchCaptureRelationSchema
          // shape (zod validated every item's {to, type} before this handler ran);
          // apply here means "create against the kernel," not "shape validation."
          let relationsCreatedCount = 0;
          let relationsPreviewedCount = 0;
          let relationsProposedCount = 0;
          const relationsFailedList: string[] = [];
          if (entry.relations && entry.relations.length > 0) {
            for (const rel of entry.relations) {
              try {
                // Finding #14: forward `preview` (chain.createEntryRelation DOES support it,
                // unlike the plural batch mutation above) and branch on the returned status
                // instead of discarding the result — a discarded result treated EVERY
                // non-throwing call as "created", including preview dry-runs (no write) and
                // misuse-pattern interceptions that only file an agent proposal (no edge
                // written) — both silently inflated relationsCreated in the response.
                const relResult = await kernelMutation<{ status?: string; proposalId?: string; preview?: boolean }>(
                  "chain.createEntryRelation",
                  {
                    fromEntryId: finalEntryId,
                    toEntryId: rel.to,
                    type: rel.type,
                    proposedBy: "user",
                    sessionId: agentId ?? undefined,
                    ...(preview ? { preview: true } : {}),
                  },
                );
                if (relResult?.preview) {
                  relationsPreviewedCount++;
                } else if (relResult?.status === "agent_proposal_created") {
                  relationsProposedCount++;
                } else {
                  relationsCreatedCount++;
                }
              } catch (relErr: unknown) {
                const relMsg = relErr instanceof Error ? relErr.message : String(relErr);
                relationsFailedList.push(`${rel.to} (${rel.type}): ${relMsg}`);
              }
            }
            if (relationsFailedList.length > 0) {
              batchEntryWarnings.push(`${relationsFailedList.length} inline relation(s) failed: ${relationsFailedList.join("; ")}`);
            }
            if (relationsPreviewedCount > 0) {
              batchEntryWarnings.push(`${relationsPreviewedCount} inline relation(s) would be created (preview — no DB writes).`);
            }
            if (relationsProposedCount > 0) {
              batchEntryWarnings.push(`${relationsProposedCount} inline relation(s) converted to an agent proposal (misuse pattern) — review in Cortex UI.`);
            }
          }

          // DEC-896 / BR-76 S3: skip commitEntry when server already returned active.
          if (autoCommitApplied && !batchWasAutoCommittedServerSide) {
            try {
              const semanticConflicts = await runSemanticConflictPreflight(entry.name, entry.description, resolvedSlug);
              const blockingConflicts = semanticConflicts.filter(
                (conflict) => conflict.type === "contradiction" && conflict.confidence >= BLOCKING_CONTRADICTION_THRESHOLD,
              );

              if (blockingConflicts.length > 0) {
                commitError = `blocked by semantic contradiction preflight: ${blockingConflicts[0]?.chainEntryId} ${blockingConflicts[0]?.explanation ?? ""}`.trim();
              } else {
                const commitResult = await kernelMutation<any>("chain.commitEntry", {
                  entryId: finalEntryId,
                  author: agentId ? `agent:${agentId}` : undefined,
                  sessionId: agentId ?? undefined,
                });
                const refusalReason = coherencyRefusalReason(commitResult);
                if (refusalReason) {
                  // WP-465 slice ⑤ (Option B): the entry stays a draft — never accepted/tracked.
                  commitError = `coherency gate refused this accept — ${refusalReason}`;
                  // WP-465 surface parity: thread full refusal payload so routes + mode reach the caller.
                  commitRefusal = (commitResult?.refusal as Record<string, unknown> | undefined) ?? undefined;
                  finalStatus = "draft_on_failure";
                } else {
                  finalStatus = commitResult?.status === "proposal_created" ? "proposed" : "committed";
                  if (finalStatus === "committed") {
                    await recordSessionActivity({ entryModified: internalId });
                    trackChainEntryCommitted(wsCtx.workspaceId, {
                      entry_id: finalEntryId,
                      collection: resolvedSlug ?? undefined,
                      commit_method: "auto",
                      surface: "mcp_capture",
                    });
                  }
                }
              }
            } catch (error: unknown) {
              // BR-76 S2 — DEC-888: Surface failures via log + payload + session TEN. Never silent.
              commitError = error instanceof Error ? error.message : String(error);
              finalStatus = "draft_on_failure";
              await recordCommitFailure({ entryId: finalEntryId, error, sessionId: agentId, server });
            }
          }

          const entryNorm = result.normalization;
          results.push({
            name: entry.name,
            collection: resolvedSlug,
            entryId: finalEntryId,
            ok: true,
            autoLinks: autoLinkCount,
            advisedLinks: advisedLinkCount,
            status: finalStatus,
            classifiedBy,
            confidence,
            confidenceTier,
            ...(commitError ? { commitError } : {}),
            // WP-465 surface parity: thread full refusal payload for coherency-refused batch entries.
            ...(commitRefusal ? { commitRefusal } : {}),
            ...(batchEntryWarnings.length > 0 ? { warnings: batchEntryWarnings } : {}),
            ...(entryNorm && (Object.keys(entryNorm.remapped).length > 0 || entryNorm.rejected.length > 0) && {
              normalization: { remapped: entryNorm.remapped, rejected: entryNorm.rejected },
            }),
            // BET-167: Track overlap count for conflict advisory
            ...(entryOverlapCount > 0 ? { overlapCount: entryOverlapCount } : {}),
            // TEN-2365: capture-time authority-domain proposal echo.
            ...(result.authorityDomain ? { authorityDomain: result.authorityDomain } : {}),
            // TEN-957 (WP-484 S2): inline relations created for this entry.
            ...(relationsCreatedCount > 0 ? { relationsCreated: relationsCreatedCount } : {}),
            // Finding #14: distinct from relationsCreated — never counted as a write.
            ...(relationsProposedCount > 0 ? { relationsProposed: relationsProposedCount } : {}),
          });
        } catch (error: unknown) {
          const msg = error instanceof Error ? error.message : String(error);
          results.push({
            name: entry.name,
            collection: resolvedSlug,
            entryId: "",
            ok: false,
            autoLinks: 0,
            advisedLinks: 0,
            status: "draft",
            classifiedBy,
            confidence,
            confidenceTier,
            error: msg,
          });
        }
      }

      const created = results.filter((r) => r.ok);
      const failed = results.filter((r) => !r.ok);
      const committed = created.filter((r) => r.status === "committed");
      const proposed = created.filter((r) => r.status === "proposed");
      const drafts = created.filter((r) => r.status === "draft");
      const commitFailed = created.filter((r) => r.status === "draft_on_failure");
      const classifiedCount = created.filter((r) => r.classifiedBy && r.classifiedBy !== "explicit").length;

      await server.sendLoggingMessage({
        level: "info",
        data: `Batch complete. ${created.length} succeeded, ${failed.length} failed, ${skippedLowConfidence.length} skipped (low confidence).`,
        logger: "product-brain",
      });

      const totalAutoLinks = created.reduce((sum, r) => sum + r.autoLinks, 0);
      // WP-474 E5: aggregate advised-not-written cohort across the batch — never silently dropped.
      const totalAdvisedLinks = created.reduce((sum, r) => sum + r.advisedLinks, 0);

      const byCollection = new Map<string, number>();
      for (const r of created) {
        byCollection.set(r.collection, (byCollection.get(r.collection) ?? 0) + 1);
      }

      const lines: string[] = [
        `# Batch Capture Complete`,
        `**${created.length}** created, **${failed.length}** failed, **${skippedLowConfidence.length}** skipped out of ${entries.length} total.`,
        `**Auto-links created:** ${totalAutoLinks}${totalAdvisedLinks > 0 ? `, ${totalAdvisedLinks} advised` : ""}`,
        "",
      ];

      if (classifiedCount > 0) {
        lines.push(`**Auto-classified:** ${classifiedCount} entries routed by LLM/heuristic.`);
        lines.push("");
      }

      if (created.length > 0) {
        const commitFailedSegment = commitFailed.length > 0 ? `, ${commitFailed.length} commit-failed` : "";
        lines.push(
          `**Statuses:** ${committed.length} committed, ${proposed.length} proposed, ${drafts.length} draft${commitFailedSegment}.`,
        );
        lines.push("");
      }

      if (byCollection.size > 0) {
        lines.push("## By Collection");
        for (const [col, count] of byCollection) {
          lines.push(`- \`${col}\`: ${count} entries`);
        }
        lines.push("");
      }

      if (created.length > 0) {
        const highConfidence = created.filter((r) => r.confidenceTier === "high" || r.classifiedBy === "explicit");
        const mediumConfidence = created.filter((r) => r.confidenceTier === "medium");

        if (highConfidence.length > 0) {
          lines.push("## Created — High Confidence / Explicit");
          for (const r of highConfidence) {
            const linkNote = r.autoLinks > 0 ? `, ${r.autoLinks} auto-links` : "";
            const classNote = r.classifiedBy !== "explicit" ? ` (${r.classifiedBy} ${r.confidence}%)` : "";
            lines.push(`- **${r.entryId}**: ${r.name} [${r.collection}] — \`${r.status}\`${classNote}${linkNote}`);
          }
        }

        if (mediumConfidence.length > 0) {
          lines.push("");
          lines.push("## Created — Medium Confidence (review recommended)");
          for (const r of mediumConfidence) {
            const linkNote = r.autoLinks > 0 ? `, ${r.autoLinks} auto-links` : "";
            lines.push(`- **${r.entryId}**: ${r.name} [${r.collection}] — \`${r.status}\` (${r.classifiedBy} ${r.confidence}%)${linkNote}`);
          }
          lines.push(`\n_Use \`entries action=move\` to correct any misclassified entries._`);
        }
      }

      if (skippedLowConfidence.length > 0) {
        lines.push("");
        lines.push("## Skipped — Low Confidence (needs explicit collection)");
        for (const s of skippedLowConfidence) {
          const suggestion = s.suggestedCollection
            ? ` — best guess: \`${s.suggestedCollection}\` (${s.confidence}%)`
            : " — no classification available";
          const alts = s.alternatives?.length
            ? ` | alternatives: ${s.alternatives.map((a) => `\`${a.collection}\` (${a.confidence}%)`).join(", ")}`
            : "";
          lines.push(`- **[${s.index}]** ${s.name}${suggestion}${alts}`);
        }
        lines.push("");
        lines.push("_Re-capture these with an explicit `collection` or use the suggested collection._");
      }

      if (commitFailed.length > 0) {
        lines.push("");
        lines.push("## Saved as draft");
        for (const r of commitFailed) {
          if (r.commitRefusal) {
            // WP-465 surface parity: mirror the single path — the refusal block already leads with
            // the reason, so DROP commitError from the bullet (it embeds the same reason → no double
            // print). Render the block at top level (NOT indented — indenting turns the `##`/`###`
            // headings into a code block) and demote the leading heading to H3.
            lines.push(`- **${r.entryId}**: ${r.name} [${r.collection}]`);
            const refusalText = coherencyRefusedResult("batch-capture", r.entryId ?? "", r.commitRefusal, { headingLevel: 3 }).content[0].text;
            lines.push(...refusalText.split("\n"));
          } else {
            lines.push(`- **${r.entryId}**: ${r.name} [${r.collection}] — ${r.commitError}`);
          }
        }
      }

      // BET-167: Advisory overlap notice for batch entries with potential duplicates
      if (autoCommitApplied) {
        const entriesWithOverlaps = created.filter((r) => r.overlapCount && r.overlapCount > 0);
        if (entriesWithOverlaps.length > 0) {
          lines.push("");
          lines.push("## Potential Overlaps");
          lines.push(`${entriesWithOverlaps.length} ${entriesWithOverlaps.length === 1 ? "entry has" : "entries have"} similar existing entries. Consider reviewing for duplicates:`);
          for (const r of entriesWithOverlaps) {
            lines.push(`- **${r.entryId}**: ${r.name} — ${r.overlapCount} similar ${r.overlapCount === 1 ? "entry" : "entries"}`);
          }
        }
      }

      const entriesWithWarnings = created.filter((r) => r.warnings && r.warnings.length > 0);
      if (entriesWithWarnings.length > 0) {
        lines.push("");
        lines.push("## Validation Warnings");
        for (const r of entriesWithWarnings) {
          lines.push(`- **${r.entryId}** (${r.name}): ${r.warnings!.join("; ")}`);
        }
      }

      if (failed.length > 0) {
        lines.push("");
        lines.push("## Failed");
        for (const r of failed) {
          lines.push(`- ${r.name} [${r.collection}]: _${r.error}_`);
        }
        lines.push("");
        lines.push(`_If failed > 0, inspect \`failedEntries\` in the structured response and retry individually._`);
      }

      const entryIds = created.map((r) => r.entryId);
      if (entryIds.length > 0 || skippedLowConfidence.length > 0) {
        lines.push("");
        lines.push("## Next Steps");
        if (entryIds.length > 0) {
          lines.push(`- **Connect:** Run \`relations action=suggest\` on key entries to build the knowledge graph`);
        }
        if (drafts.length > 0) {
          lines.push(`- **Accept:** Use \`entries action=commit\` to promote remaining drafts to SSOT`);
        }
        if (skippedLowConfidence.length > 0) {
          lines.push(`- **Classify:** Re-capture ${skippedLowConfidence.length} skipped entries with explicit collections`);
        }
        if (entryIds.length > 0) {
          lines.push(`- **Quality:** Run \`quality action=check\` on individual entries to assess completeness`);
        }
      }

      const skippedNote = skippedLowConfidence.length > 0 ? `, ${skippedLowConfidence.length} skipped (low confidence)` : "";
      const classifiedNote = classifiedCount > 0 ? `, ${classifiedCount} auto-classified` : "";
      const commitFailedNote = commitFailed.length > 0 ? `, ${commitFailed.length} commit-failed` : "";
      const summary =
        failed.length > 0 || skippedLowConfidence.length > 0 || commitFailed.length > 0
          ? `Batch captured ${created.length}/${entries.length} entries (${failed.length} failed${skippedNote}, ${committed.length} committed, ${proposed.length} proposed, ${drafts.length} draft${commitFailedNote}${classifiedNote}).`
          : `Batch captured ${created.length} entries successfully (${committed.length} committed, ${proposed.length} proposed, ${drafts.length} draft${classifiedNote}).`;

      const firstDraft = drafts[0];

      const next: NextAction[] = [];
      if (created.length > 0) {
        next.push({ tool: "relations", description: "Discover connections", parameters: { action: "suggest", entryId: created[0].entryId } });
      }
      if (firstDraft) {
        next.push({ tool: "entries" as const, description: "Accept first draft", parameters: { action: "commit", entryId: firstDraft.entryId } });
      }
      if (skippedLowConfidence.length > 0) {
        next.push({ tool: "capture", description: `Capture skipped entry with explicit collection`, parameters: { name: skippedLowConfidence[0].name, collection: skippedLowConfidence[0].suggestedCollection ?? "" } });
      }

      const batchTotalMs = Date.now() - batchTimingStart;
      const batchTiming = {
        totalMs: batchTotalMs,
        perEntryAvgMs: created.length > 0 ? Math.round(batchTotalMs / created.length) : 0,
      };

      return {
        content: [{ type: "text" as const, text: lines.join("\n") }],
        structuredContent: {
          ...success(
            summary,
            {
              captured: created.map((r) => ({
              entryId: r.entryId,
              collection: r.collection,
              name: r.name,
              status: r.status,
              ...(r.classifiedBy ? { classifiedBy: r.classifiedBy } : {}),
              ...(r.confidence != null ? { confidence: r.confidence } : {}),
              ...(r.confidenceTier ? { confidenceTier: r.confidenceTier } : {}),
              ...(r.warnings?.length ? { warnings: r.warnings } : {}),
              ...(r.normalization ? { normalization: r.normalization } : {}),
              ...(r.commitError ? { commitError: r.commitError } : {}),
              // WP-465 surface parity: thread full refusal payload into batch structured output.
              ...(r.commitRefusal ? { coherencyRefusal: r.commitRefusal } : {}),
              ...(r.authorityDomain ? { domain: r.authorityDomain.slug } : {}),
              // TEN-957 (WP-484 S2): inline relations created for this entry.
              ...(r.relationsCreated ? { relationsCreated: r.relationsCreated } : {}),
              // Finding #14: proposal outcomes are distinct from writes — never folded in.
              ...(r.relationsProposed ? { relationsProposed: r.relationsProposed } : {}),
            })),
            total: created.length,
            failed: failed.length,
            committed: committed.length,
            proposed: proposed.length,
            drafts: drafts.length,
            ...(commitFailed.length > 0 ? { commitFailed: commitFailed.length } : {}),
            ...(classifiedCount > 0 ? { classified: classifiedCount } : {}),
            autoCommitApplied,
            ...(skippedLowConfidence.length > 0 && {
              skippedLowConfidence: skippedLowConfidence.map((s) => ({
                index: s.index,
                name: s.name,
                ...(s.suggestedCollection ? { suggestedCollection: s.suggestedCollection } : {}),
                ...(s.confidence != null ? { confidence: s.confidence } : {}),
                ...(s.alternatives?.length ? { alternatives: s.alternatives } : {}),
              })),
            }),
            ...(failed.length > 0 && {
              failedEntries: failed.map((r) => ({
                index: results.indexOf(r),
                collection: r.collection,
                name: r.name,
                error: r.error ?? "unknown error",
              })),
            }),
          },
          next,
        ),
          _meta: { timing: batchTiming },
        },
      };
}

// ── Helpers ────────────────────────────────────────────────────────────────

/**
 * WP-465 slice ⑤ (Option B): `chain.commitEntry` RETURNS a structured coherency refusal
 * instead of throwing. Detect it so an auto-commit refusal is surfaced as a non-committed
 * draft (mirrors the thrown-failure path) and is NEVER reported or tracked as accepted.
 * Returns the refusal reason when refused, otherwise null.
 */
function coherencyRefusalReason(
  commitResult: { status?: string; refusal?: { reason?: string } } | null | undefined,
): string | null {
  if (commitResult?.status !== "coherency_refused") return null;
  return commitResult.refusal?.reason ?? "refused by the coherency gate";
}

/**
 * BR-76 S2 — DEC-888: Surface commitEntry failures so they are never silent.
 * Fire-and-forget: each fan-out step has its own try/catch so a failure in one
 * does NOT block the others and does NOT change the caller's finalStatus.
 *
 * Steps:
 *  1. server.sendLoggingMessage (level "error") — visible in MCP transcript stream.
 *  2. Session-bound TEN emit via chain.createEntry — persisted to Chain when an
 *     active write session (agentId) exists.
 *
 * Persistence to a queryable DB field is deferred (TEN-1811 follow-on).
 */
async function recordCommitFailure({
  entryId,
  error,
  sessionId,
  server,
}: {
  entryId: string;
  error: unknown;
  sessionId: string | null | undefined;
  server: McpServer;
}): Promise<void> {
  const errorMessage = error instanceof Error ? error.message : String(error);
  const logLine = `commitEntry failed for ${entryId}: ${errorMessage}${sessionId ? ` (session: ${sessionId})` : ""}`;

  // Step 1: MCP transcript log — level "error"
  try {
    await server.sendLoggingMessage({ level: "error", data: logLine, logger: "product-brain" });
  } catch {
    // Logging must never break the caller
  }

  // Step 2: Session-bound TEN emit — only when an active write session exists
  if (sessionId) {
    try {
      const tenName = `commitEntry failed for ${entryId} — ${errorMessage}`.slice(0, 250);
      await kernelMutation("chain.createEntry", {
        collectionSlug: "tensions",
        name: tenName,
        // Failure audit TENs intentionally stay draft — they need explicit human review,
        // not auto-commit, even in Open mode.
        status: "draft",
        data: {},
        createdBy: `agent:${sessionId}`,
        sessionId,
      });
    } catch {
      // TEN emit is advisory — never propagates
    }
  }
}

// Stop words excluded from noun phrase extraction
const STOP_WORDS = new Set([
  "the", "and", "for", "are", "but", "not", "you", "all", "can", "has", "her",
  "was", "one", "our", "out", "day", "had", "hot", "how", "its", "may", "new",
  "now", "old", "see", "way", "who", "did", "get", "let", "say", "she", "too",
  "use", "from", "have", "been", "each", "that", "this", "with", "will", "they",
  "what", "when", "make", "like", "long", "look", "many", "some", "them", "than",
  "most", "only", "over", "such", "into", "also", "back", "just", "much", "must",
  "name", "very", "your", "after", "which", "their", "about", "would", "there",
  "should", "could", "other", "these", "first", "being", "those", "still", "where",
]);

function tokenizeText(input: string): string[] {
  return input
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s]+/gu, " ")
    .split(/\s+/)
    .filter(Boolean);
}

/**
 * Keyword contradiction check against governance entries.
 * Extracts terms 4+ chars, searches architecture and business-rules,
 * checks for 'governs' relations, returns formatted warnings.
 * Non-blocking — never throws, never prevents the operation.
 */
export interface ContradictionWarning {
  entryId: string;
  name: string;
  collection: string;
  governsCount: number;
}

export async function runContradictionCheck(
  name: string,
  description: string,
): Promise<ContradictionWarning[]> {
  const warnings: ContradictionWarning[] = [];
  try {
    const keyTerms = tokenizeText(`${name} ${description}`)
      .filter((w) => w.length >= 4 && !STOP_WORDS.has(w))
      .slice(0, 8);

    if (keyTerms.length === 0) return warnings;

    const searchQuery = keyTerms.slice(0, 5).join(" ");
    const [govResults, archResults] = await Promise.all([
      kernelQuery<any[]>("chain.searchEntries", { query: searchQuery, collectionSlug: "business-rules" }),
      kernelQuery<any[]>("chain.searchEntries", { query: searchQuery, collectionSlug: "architecture" }),
    ]);

    const allGov = [...(govResults ?? []), ...(archResults ?? [])].slice(0, 5);

    for (const entry of allGov) {
      const entryTokens = new Set(tokenizeText(`${entry.name} ${entry.data?.description ?? ""}`));
      const matched = keyTerms.filter((t) => entryTokens.has(t));
      if (matched.length < 3) continue;

      // Check for 'governs' relations
      let governsCount = 0;
      try {
        const relations = await kernelQuery<any[]>("chain.listEntryRelations", {
          entryId: entry.entryId,
        });
        governsCount = (relations ?? []).filter((r: any) => r.type === "governs").length;
      } catch { /* non-critical */ }

      warnings.push({
        entryId: entry.entryId ?? "",
        name: entry.name,
        collection: entry.collectionSlug ?? "",
        governsCount,
      });
    }
  } catch {
    // Contradiction check is advisory — never blocks the operation
  }
  return warnings;
}

interface SemanticConflictResult {
  chainEntryId: string;
  chainEntryName: string;
  type: "contradiction" | "supersession" | "duplicate" | "overlap";
  confidence: number;
  explanation: string;
}

const BLOCKING_CONTRADICTION_THRESHOLD = 0.75;

async function runSemanticConflictPreflight(
  name: string,
  description: string,
  collectionHint?: string,
): Promise<SemanticConflictResult[]> {
  try {
    const conflicts = await kernelQuery<SemanticConflictResult[] | null>("chain.detectSemanticConflicts", {
      name,
      description,
      ...(collectionHint ? { collectionHint } : {}),
    });
    return conflicts ?? [];
  } catch {
    return [];
  }
}

// ── Quality Coaching Helpers ──────────────────────────────────────────────

// WP-475 E1 (Copilot review): typed structural subset of the kernel QualityVerdict
// (kernel/quality/types.ts) — exactly the fields this tool consumes (render via
// formatRubricCoaching, telemetry via trackQualityVerdict, envelope qualityVerdict).
// Precedent: the CLI's local mirror in packages/cli/src/formatters/coaching.ts.
// The runtime value may carry more fields; they pass through the envelope spread
// untouched.
interface CoachingCriterionSubset {
  id: string;
  passed: boolean;
  hint: string;
  questionTemplate: string;
}

interface CoachingVerdictSubset {
  canonicalKey: string;
  tier: string;
  passed: boolean;
  criteria: CoachingCriterionSubset[];
  weakest: { criterion: string; hint: string; questionTemplate: string } | null;
  /** WP-475 E3: rubric methodology that produced the verdict (v1-vs-v2 seam). */
  methodologyVersion?: string;
}

interface CoachingResultSubset {
  verdict: CoachingVerdictSubset;
  source: string;
}

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) =>
      setTimeout(() => reject(new Error("quality coaching timeout")), ms),
    ),
  ]);
}

/**
 * WP-480 S1 (FEAT-1358): a short capture-confirmation phrase derived from the
 * SERVER rubric verdict — replaces the deleted client `Quality N/10` recompute
 * (single scorer; DEC-1418 the connector owns the sentence). Passive/absent
 * verdicts (no rubric criteria) read as "not evaluated"; otherwise it reports the
 * rubric criteria pass count and points at the coaching already in the response.
 */
export function verdictQualityPhrase(
  result: { verdict?: { tier?: string; passed?: boolean; criteria?: Array<{ passed: boolean }> } } | null | undefined,
): string {
  const v = result?.verdict;
  const criteria = v?.criteria ?? [];
  // "No rubric" is decided by whether the verdict HAS criteria — never by its tier.
  // Passive-tier types (tension, glossary) carry real rubric criteria, and for them the
  // heuristic IS the full judgment (the background LLM pass deliberately never runs).
  // Short-circuiting on tier === 'passive' therefore threw away a real verdict and told
  // the agent "not evaluated" while qualityVerdict recorded failing criteria.
  if (!v || criteria.length === 0) {
    return "Quality: not evaluated (no rubric for this type).";
  }
  const total = criteria.length;
  const passed = criteria.filter((c) => c.passed).length;
  return v.passed
    ? `Quality: all ${total} rubric criteria pass.`
    : `Quality: ${passed}/${total} rubric criteria pass — see the coaching above.`;
}

/**
 * Format a full quality coaching result (from evaluateAtCapture / evaluateAtCommit)
 * for inclusion in MCP capture/commit responses.
 */
export function formatRubricCoaching(result: any): string {
  const { verdict, rogerMartin } = result;
  if (!verdict || verdict.criteria.length === 0) return "";

  const lines: string[] = ["## Semantic Quality"];
  const failed = (verdict.criteria ?? []).filter((c: any) => !c.passed);
  const total = verdict.criteria?.length ?? 0;
  const passedCount = total - failed.length;

  if (verdict.passed) {
    lines.push(`All ${total} rubric criteria pass for \`${verdict.canonicalKey}\` (${verdict.tier} tier).`);
  } else {
    lines.push(`${passedCount}/${total} criteria pass for \`${verdict.canonicalKey}\` (${verdict.tier} tier)`);
    lines.push("");
    for (const c of verdict.criteria) {
      const icon = c.passed ? "[x]" : "[ ]";
      const extra = c.passed ? "" : ` — ${c.hint}`;
      lines.push(`${icon} ${c.id}${extra}`);
    }

    if (verdict.weakest) {
      lines.push("");
      lines.push(`**Coaching hint:** ${verdict.weakest.hint}`);
      lines.push(`_Question to consider:_ ${verdict.weakest.questionTemplate}`);
    }
  }

  if (rogerMartin) {
    lines.push("");
    lines.push("### Roger Martin Test");
    if (rogerMartin.isStrategicChoice) {
      lines.push("This principle passes — the opposite is a reasonable strategic choice.");
    } else {
      lines.push(`This principle may not be a strategic choice. ${rogerMartin.reasoning}`);
      if (rogerMartin.suggestion) {
        lines.push(`_Suggestion:_ ${rogerMartin.suggestion}`);
      }
    }
  }

  return lines.join("\n");
}

/**
 * Format a cached rubric verdict section for quality check responses.
 * SOS-e4r5cn: capability and visibility must ship together.
 */
export function formatRubricVerdictSection(verdict: any): string {
  if (!verdict || !verdict.criteria || verdict.criteria.length === 0) return "";

  const lines: string[] = ["## Semantic Quality"];
  const failed = verdict.criteria.filter((c: any) => !c.passed);
  const total = verdict.criteria.length;
  const passedCount = total - failed.length;

  const durationSuffix = verdict.llmDurationMs ? ` in ${(verdict.llmDurationMs / 1000).toFixed(1)}s` : '';
  const statusNote = verdict.llmStatus === 'pending'
    ? ' — LLM evaluation in progress...'
    : verdict.llmStatus === 'failed'
      ? ` — LLM evaluation failed${verdict.llmError ? `: ${verdict.llmError}` : ''}, showing heuristic results`
      : verdict.source === 'llm' && durationSuffix
        ? ` — evaluated${durationSuffix}`
        : '';

  if (failed.length === 0) {
    lines.push(`All ${total} rubric criteria pass for \`${verdict.canonicalKey}\` (${verdict.tier} tier, ${verdict.source} evaluation).${statusNote}`);
  } else {
    lines.push(`${passedCount}/${total} criteria pass for \`${verdict.canonicalKey}\` (${verdict.tier} tier, ${verdict.source} evaluation)${statusNote}`);
    lines.push("");
    for (const c of verdict.criteria) {
      const icon = c.passed ? "[x]" : "[ ]";
      const extra = c.passed ? "" : ` — ${c.hint}`;
      lines.push(`${icon} ${c.id}${extra}`);
    }

    if (verdict.weakest) {
      lines.push("");
      lines.push(`**Top improvement:** ${verdict.weakest.hint}`);
    }
  }

  if (verdict.rogerMartin) {
    const rm = verdict.rogerMartin;
    lines.push("");
    lines.push("### Roger Martin Test");
    if (rm.isStrategicChoice) {
      lines.push(`This is a real strategic choice — the opposite is reasonable. ${rm.reasoning}`);
    } else {
      lines.push(`This may not be a strategic choice. ${rm.reasoning}`);
      if (rm.suggestion) {
        lines.push(`_Suggestion:_ ${rm.suggestion}`);
      }
    }
  }

  return lines.join("\n");
}
